import React, { useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { differenceInDays } from "date-fns";

import { API, graphqlOperation } from "aws-amplify";
import {
  getInventorie,
  getInventorieReleaseState,
  getInventorieValidate,
  getInventoryItemValidate,
  outboundInventoryByStatusByAccountIdByProductByLot,
} from "../../../graphql/inventory/custom-queries";
import { getOutboundShipmentValidate } from "../../../graphql/outbound/custom-queries";
import {
  outboundShipmentByAccountByConsigneePoNumber,
  outboundShipmentByAccountByPickupNumber,
  outboundShipmentByAccountByPoNumber,
} from "../../../graphql/queries";
import { getBOLCounter } from "../../../graphql/reloadyard/custom-queries";
import { updateReloadYardMini } from "../../../graphql/reloadyard/custom-mutations";
import {
  buildOutboundScheduleUpdateInput,
  buildTrackCarCreateInput,
  buildTrackCarUpdateInput,
  sanitizeOutbound,
  sanitizeOutboundLambda,
} from "../../../graphql/outbound/outboundCRUD";

import {
  Box,
  Grid,
  makeStyles,
  Paper,
  Tooltip,
  Typography,
} from "@material-ui/core";

import {
  BooleanInput,
  Button,
  Confirm,
  Datagrid,
  FunctionField,
  IconButtonWithTooltip,
  ListContextProvider,
  maxValue,
  minValue,
  number,
  NumberField,
  NumberInput,
  required,
  TextField,
  TextInput,
  useList,
} from "react-admin";

import {
  ArrowDownwardIcon,
  ArrowUpwardIcon,
  DeleteIcon,
  EditIcon,
  InfoOutlinedIcon,
  RemoveIcon,
  ExceptionIcon,
} from "../../../components/common/iconography";
import { ProductLinkIcon } from "../../../components/common/ProductLinkIcon";
import { AddressAutocomplete } from "../../../components/common/AddressAutocomplete";
import {
  CarrierType,
  InboundState,
  InboundStatus,
  InventoryStatus,
  LocationType,
  LotType,
  MAX_INVENTORY_QUERY_LIMIT,
  NoteType,
  OutboundState,
  OutboundStatus,
  OutboundStatusLabels,
  POSDefaultSubject,
  ResourceType,
  ShipmentLineItemStatus,
  AccountType,
  SORTKEY_BUFFER,
  TransferOptions,
  TransferType,
  Unit,
  VALIDATION_MSG,
} from "../../../utils/constants";

import { logError } from "../../../utils/appUtils";
import {
  isBulkArea,
  isBulkLocation,
  isManagedLocation,
} from "../../../utils/locationUtils";
import {
  excludeReleasedRailcarInventories,
  isInventoryItemOnReleasedRailcar,
  releasedRailcarCommitRefusal,
  releasedRailcarLabel,
} from "../../../utils/releasedRailcar";
import {
  convertUnits,
  fetchCarrierEquipmentTypes,
  getUnitConversions,
  isBulkCommodity,
  isWeightUOM,
  getUnitName,
} from "../../../utils/loadTypeUtils";
import {
  createLambdaAppointment,
  deleteLambdaAppointment,
  toComparableScheduledDateTime,
  updateLambdaAppointment,
} from "../../../utils/dateUtils";
import {
  deepRedirect,
  isEmptyAddress,
  isFullAddress,
  trimDecimal,
  formatCurrency,
  splitEmails,
  isAddressEqual,
  generateEmailSubject,
  normalizeValue,
} from "../../../utils/utils";

import { getOutboundPOSBody } from "./POS/POSBody";
import { sendEmail } from "../../../utils/zapierUtils";
import { orange } from "@material-ui/core/colors";
import {
  BillingAutomation,
  ChargeEvent,
  StorageFrequency,
} from "../accounts/chargeRules/chargeRuleConstants";
import { getUnitPrice } from "../services/ServiceUtils";
import { createLineItemsForOutbound } from "../invoices/InvoiceUtils";
import { isBrokeredAccount, isInternalAccount } from "../accounts/AccountUtils";

const useStyles = makeStyles((theme) => ({
  actionButtons: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "end",
    alignItems: "flex-end",
  },
  paper: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(1),
    paddingLeft: theme.spacing(8),
  },
  reorderButtons: {
    paddingRight: theme.spacing(1),
  },
  icons: {
    position: "relative",
    top: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
    color: orange[600],
  },
}));

const validateQuantity = (min, max) => [
  required(),
  number(),
  minValue(min),
  maxValue(max, "Exceeds Available"),
];

export const formValidate = (form, notify) => {
  const formState = form.getState();
  if (formState?.invalid) notify(VALIDATION_MSG, "warning");
  return formState?.invalid !== true;
};

/** Form `source` names for outbound reference duplicate checks. */
export const OUTBOUND_REFERENCE_NUMBER_SOURCES = [
  "poNumber",
  "consigneePoNumber",
  "pickupNumber",
];

/** Labels for validateOutboundUnicity / save confirm (human-readable). */
export const OUTBOUND_DUPLICATE_REF = {
  poNumber: "PO",
  consigneePoNumber: "Consignee PO",
  pickupNumber: "Pickup",
};

const fetchDuplicateOutboundForReferenceQuery = async (
  formData,
  rawValue,
  normalizedField,
  normalizedValue,
  query,
  queryName,
) => {
  const accountId = formData.accountId;
  const hasRawValue = String(rawValue || "").trim().length > 0;

  if (!accountId || !hasRawValue || !normalizedValue.trim()) return false;

  try {
    const filter = formData.id ? { id: { ne: formData.id } } : undefined;

    const response = await API.graphql(
      graphqlOperation(query, {
        accountId,
        [normalizedField]: { eq: normalizedValue },
        filter,
        limit: 2,
      }),
    );

    return response?.data?.[queryName]?.items?.length > 0;
  } catch (error) {
    logError(
      "outboundShipments:outboundUtils:fetchDuplicateOutboundForReferenceQuery",
      error,
      false,
    );
    return false;
  }
};

/**
 * Reference duplicate checks use the normalized key queries declared on OutboundShipment.
 * Pickup is checked whenever `pickupNumber` is set, including auto-generated values.
 */
const fetchDuplicateOutboundByNormalizedPoNumber = (formData) =>
  fetchDuplicateOutboundForReferenceQuery(
    formData,
    formData.poNumber,
    "normalizedPoNumber",
    normalizeValue(formData.poNumber),
    outboundShipmentByAccountByPoNumber,
    "outboundShipmentByAccountByPoNumber",
  );

const fetchDuplicateOutboundByNormalizedConsigneePoNumber = (formData) =>
  fetchDuplicateOutboundForReferenceQuery(
    formData,
    formData.consigneePoNumber,
    "normalizedConsigneePoNumber",
    normalizeValue(formData.consigneePoNumber),
    outboundShipmentByAccountByConsigneePoNumber,
    "outboundShipmentByAccountByConsigneePoNumber",
  );

const fetchDuplicateOutboundByNormalizedPickupNumber = (formData) =>
  fetchDuplicateOutboundForReferenceQuery(
    formData,
    formData.pickupNumber,
    "normalizedPickupNumber",
    normalizeValue(formData.pickupNumber),
    outboundShipmentByAccountByPickupNumber,
    "outboundShipmentByAccountByPickupNumber",
  );

export const validateOutboundUnicity = async (formData) => {
  const exludeList = ["TRANSFER", "RETURN"];
  const { poNumber, consigneePoNumber, pickupNumber, accountId } = formData;
  const duplicateNumbers = [];

  if (!accountId || (!poNumber && !consigneePoNumber && !pickupNumber)) {
    return duplicateNumbers;
  }
  if (
    exludeList.includes(poNumber) ||
    exludeList.includes(consigneePoNumber) ||
    exludeList.includes(pickupNumber)
  ) {
    return duplicateNumbers;
  }

  try {
    const [poDup, consigneeDup, pickupDup] = await Promise.all([
      fetchDuplicateOutboundByNormalizedPoNumber(formData),
      fetchDuplicateOutboundByNormalizedConsigneePoNumber(formData),
      fetchDuplicateOutboundByNormalizedPickupNumber(formData),
    ]);

    if (poDup) duplicateNumbers.push(OUTBOUND_DUPLICATE_REF.poNumber);
    if (consigneeDup)
      duplicateNumbers.push(OUTBOUND_DUPLICATE_REF.consigneePoNumber);
    if (pickupDup) duplicateNumbers.push(OUTBOUND_DUPLICATE_REF.pickupNumber);
  } catch (error) {
    logError("outboundShipments:outboundUtils:validateUnicity", error, false);
  }

  return duplicateNumbers;
};

/** Map human labels from validateOutboundUnicity to form `source` names for duplicate UI state. */
export const outboundDuplicateLabelsToFieldSources = (duplicateLabels) => {
  if (!duplicateLabels?.length) return [];
  return Object.entries(OUTBOUND_DUPLICATE_REF)
    .filter(([, label]) => duplicateLabels.includes(label))
    .map(([src]) => src);
};

/** `duplicateReferenceFields` is an array of form field sources (e.g. `"poNumber"`). */
export const isOutboundDuplicateReferenceField = (
  duplicateReferenceSources,
  fieldSource,
) => Boolean(duplicateReferenceSources?.includes(fieldSource));

/** Helper text under one input when that field alone is duplicated on blur. */
export const duplicateReferenceFieldHelperText = (
  duplicateReferenceSources,
  fieldSource,
) => {
  if (isOutboundDuplicateReferenceField(duplicateReferenceSources, fieldSource))
    return "⚠️ Duplicate reference number";
  return false;
};

/** Save / create confirm dialog line (same labels as validateOutboundUnicity). */
export const formatOutboundDuplicateSaveMessage = (duplicateNumbers) =>
  `Possible duplicate reference (${duplicateNumbers.join(", ")}). Saving may create a duplicate.`;

/**
 * Blur validation: single GraphQL query for `normalizedFieldSource` only.
 * Returns whether that field is a duplicate.
 */
export const validateRefernceNumberUnicity = async (
  formData,
  normalizedFieldSource,
  notify,
) => {
  try {
    let isDuplicate = false;

    if (normalizedFieldSource === "normalizedPoNumber") {
      isDuplicate = await fetchDuplicateOutboundByNormalizedPoNumber(formData);
    } else if (normalizedFieldSource === "normalizedConsigneePoNumber") {
      isDuplicate =
        await fetchDuplicateOutboundByNormalizedConsigneePoNumber(formData);
    } else if (normalizedFieldSource === "normalizedPickupNumber") {
      isDuplicate =
        await fetchDuplicateOutboundByNormalizedPickupNumber(formData);
    }

    if (isDuplicate && notify) {
      notify("Duplicate reference number", "warning", {
        autoHideDuration: 15000, // in milliseconds (15 seconds)
      });
    }
    return isDuplicate;
  } catch (error) {
    logError(
      "outboundShipments:outboundUtils:validateRefernceNumberUnicity",
      error,
      false,
    );
    return false;
  }
};

export const handleSendPOS = async (
  data,
  toEmail,
  ccEmail,
  enclosePhotos,
  encloseDocs,
  notify,
  refresh,
  operatingAccount,
  subject,
) => {
  try {
    emailPOS(
      data,
      toEmail,
      ccEmail,
      enclosePhotos,
      encloseDocs,
      notify,
      operatingAccount,
      subject,
    );

    const historyNote = {
      text: `POS sent to: ${toEmail}`,
      by: window.appUser?.fullName,
      date: new Date(),
      type: NoteType.GENERIC,
    };

    const newHistory = { ...data, history: [...data.history, historyNote] };
    await updateLambdaOutboundMini(newHistory);
    refresh && refresh();
  } catch (err) {
    logError(
      "OutboundShipments:OutboundUtils:handleSendPOS",
      err,
      notify,
      null,
    );
  }
};

export const statusRenderer = (record) => {
  return (
    <Typography>
      {OutboundStatusLabels[record.status]}
      {record.state === OutboundState.COMPLETED
        ? ", Inspected"
        : record.state === OutboundState.REJECTED
          ? ", Rejected"
          : record.status === OutboundStatus.LOADED && !record.state
            ? ", Pending Inspection"
            : record.status === OutboundStatus.ADVICE_PENDING
              ? "Pending Review"
              : record.status === OutboundStatus.ADVICE_REJECTED
                ? "Rejected"
                : ""}
    </Typography>
  );
};

export const copyInventoryData = (inventoryList) => {
  return inventoryList.map((inventory) => {
    // copy inventoryUOM from inventory to inventoryItems
    if (inventory.inventoryItems?.items.length > 0) {
      return {
        ...inventory,
        inventoryItems: {
          ...inventory.inventoryItems,
          items: inventory.inventoryItems.items.map((item) => ({
            ...item,
            inventoryId: inventory.id,
            inventory: {
              id: inventory.id,
              productCode: inventory.productCode,
              productName: inventory.productName,
              lotNumber: inventory.lotNumber,
              poNumber: inventory.poNumber,
              railCarNumber: inventory.railCarNumber,
              receivedAt: inventory.receivedAt,
              inventoryUOM: inventory.inventoryUOM,
              weight: inventory.weight,
              weightUOM: inventory.weightUOM,
              customFields: inventory.customFields,
            },
          })),
        },
      };
    }
    return inventory; // Return inventory unchanged if it doesn't have inventoryItems.items
  });
};

export const getInventory = async (
  inventoryId,
  setAllInventories,
  setLoaded,
  notify,
) => {
  try {
    debugger;
    const result = await API.graphql(
      graphqlOperation(getInventorie, { id: inventoryId }),
    );
    const inventoryData = copyInventoryData([result.data.getInventorie]);

    const recalculatedInventory = recalculateInventoryNumbers(inventoryData);
    setAllInventories(recalculatedInventory);
    setLoaded(true);
  } catch (error) {
    logError("outboundShipment:outboundUtils:getInventory", error, notify);
  }
};

export const getAllInventories = async (
  accountId,
  isContainerPickup,
  setAllInventories,
  setIsLoaded,
  notify,
) => {
  try {
    debugger;
    setIsLoaded(false);

    // Only STORED inventories can be shipped
    let inventoryData = [];
    let query = {
      reloadYardId: window.appUser.reloadYardId,
      statusAccountIdProductNameLotNumber: {
        beginsWith: {
          status: InventoryStatus.STORED,
          accountId: accountId,
        },
      },
      limit: MAX_INVENTORY_QUERY_LIMIT, // keep below 500 to prevent resultset truncation due to internal resultset size
    };

    let nextToken = true;
    while (nextToken) {
      const input = nextToken === true ? query : { nextToken, ...query };
      const result = await API.graphql(
        graphqlOperation(
          outboundInventoryByStatusByAccountIdByProductByLot,
          input,
        ),
      );
      const inventoryList =
        result.data.inventoryByStatusByAccountIdByProductByLot.items;

      nextToken =
        result.data.inventoryByStatusByAccountIdByProductByLot.nextToken;
      inventoryData = inventoryData.concat(copyInventoryData(inventoryList));
    }

    // Inventory still in a railcar that has left cannot be picked; see
    // utils/releasedRailcar.js. Applied before the per-item filters below so
    // an inventory emptied by it drops out rather than lingering as a
    // selectable row with nothing under it.
    inventoryData = excludeReleasedRailcarInventories(inventoryData);

    // Filter inventory items by status for each inventory
    const filteredInventories = inventoryData.map((inventory) => ({
      ...inventory,
      inventoryItems: {
        ...inventory.inventoryItems,
        items: inventory.inventoryItems.items.filter((item) => {
          if (inventory.inboundShipment?.sit) return false; // exclude SIT

          if (isContainerPickup) {
            const loc = item?.location;
            const load = loc?.locationLoad;
            if (load?.container?.selected !== true) return false;
            // ISO / BULK_MOBILE grid: only container load is selected (no dryBulk/liquidBulk).
            if (loc.type === LocationType.BULK_MOBILE) return true;
            // Tank-style locations: container + dry or liquid bulk load type.
            return (
              load?.dryBulk?.selected === true ||
              load?.liquidBulk?.selected === true
            );
          }
          // for non-container, include all items
          return true;
        }),
      },
    }));

    console.log("isContainerPickUp", isContainerPickup);
    for (const inventory of filteredInventories) {
      if (inventory.containerNumber) {
        console.log("containerNumber", inventory.containerNumber);
      }
    }

    const recalculatedInventories =
      recalculateInventoryNumbers(filteredInventories);

    setAllInventories(recalculatedInventories);
  } catch (error) {
    logError("outboundShipment:outboundUtils:getAllInventories", error, notify);
  } finally {
    setIsLoaded(true);
  }
};

/**
 * Recalculate each inventory’s numbers to fix negative quantities
 *
 * For each inventory:
 *  - Subtract the totalQuantityToPick of shipmentLineItems with status === SHIPPED from inventory.quantityReveived to recalc quantityOnHand.
 *  - Sum totalQuantityToPick of shipmentLineItems with status !== SHIPPED for inventory.quantityCommitted.
 *  - For each inventory item, sum the quantityToPick values (from shipmentLineItems.inventoryItems) for shipmentLineItems with status !== SHIPPED.
 *  - Log errors if the recalculated values do not match the values returned from the query.
 *  - If the recalculated quantityOnHand is less than or equal to the recalculated quantityCommitted, filter out that inventory and log an error.
 */
const recalculateInventoryNumbers = (inventories) => {
  return inventories.reduce((accum, inventory) => {
    const isContainerShipment = !!inventory.containerNumber;
    const skipOnHandRecalc = inventory.otherException === true;

    // 1) ALWAYS compute shippedTotal and committed totals
    let shippedTotal = 0;
    let recalculatedCommittedTotal = 0;
    const committedQuantitiesByItemId = {};

    if (inventory.shipmentLineItems?.items) {
      inventory.shipmentLineItems.items.forEach((sl) => {
        const qty = sl.totalQuantityToPick || 0;
        if (sl.status === ShipmentLineItemStatus.SHIPPED) {
          shippedTotal += qty;
        } else {
          recalculatedCommittedTotal += qty;
        }
        if (
          sl.status !== ShipmentLineItemStatus.SHIPPED &&
          sl.inventoryItems?.items
        ) {
          sl.inventoryItems.items.forEach(
            ({ inventoryItemId, quantityToPick }) => {
              committedQuantitiesByItemId[inventoryItemId] =
                (committedQuantitiesByItemId[inventoryItemId] || 0) +
                (quantityToPick || 0);
            },
          );
        }
      });
    }

    // 2) Compute exception adjustments
    const underQty =
      inventory.underShipment === true
        ? inventory.underShipmentQuantity || 0
        : 0;
    const overQty =
      inventory.overShipment === true ? inventory.overShipmentQuantity || 0 : 0;

    // 3) Recalculate on-hand unless skipping
    const actualOnHand = inventory.quantityOnHand || 0;
    const recalculatedQuantityOnHand = skipOnHandRecalc
      ? inventory.quantityOnHand
      : (inventory.quantityReceived || 0) - shippedTotal;
    // : (inventory.quantityReceived || 0) + underQty - overQty - shippedTotal;

    // 4) OnHand mismatch logging
    if (
      !skipOnHandRecalc &&
      trimDecimal(actualOnHand, 3) !==
        trimDecimal(recalculatedQuantityOnHand, 3)
    ) {
      logError("QUANTITY ON HAND MISMATCH", {
        inventoryId: inventory.id,
        actual: inventory.quantityOnHand,
        expected: recalculatedQuantityOnHand,
      });
    }

    const actualCommitted = inventory.quantityCommitted ?? 0;
    if (
      trimDecimal(actualCommitted, 3) !==
      trimDecimal(recalculatedCommittedTotal, 3)
    ) {
      logError("COMMITTED QUANTITY MISMATCH", {
        inventoryId: inventory.id,
        actual: inventory.quantityCommitted,
        expected: recalculatedCommittedTotal,
      });
    }

    // 4.5) Available mismatch logging
    const quantityAvailable = trimDecimal(actualOnHand - actualCommitted, 3);
    if (quantityAvailable < 0) {
      logError("NEGATIVE QUANTITY AVAILABLE", {
        inventoryId: inventory.id,
        onHand: inventory.quantityOnHand,
        committted: inventory.quantityCommitted,
      });
    }

    // 5) Filter & map inventoryItems
    const existingInvItems = inventory.inventoryItems || { items: [] };
    const filteredItems = existingInvItems.items.reduce((result, item) => {
      const onHand = item.quantityOnHand || 0;
      const committed = item.quantityCommitted || 0;
      const recCommitted = trimDecimal(
        committedQuantitiesByItemId[item.id] || 0,
        3,
      );

      const isShipped = item.status === InventoryStatus.SHIPPED;
      const isOverCommitted =
        !isContainerShipment &&
        (recCommitted > 0 || committed > 0) &&
        (onHand <= recCommitted || onHand <= committed);
      const isDepleted = !isContainerShipment && onHand <= 0;

      // Only keep items that are not shipped, not depleted, and not over-committed
      if (isShipped || isDepleted || isOverCommitted) {
        return result;
      }

      // Log a mismatch if the recalculated committed total doesn’t match
      const actualItemCommitted = item.quantityCommitted ?? 0;
      if (actualItemCommitted !== recCommitted) {
        logError("INVENTORY ITEM COMMITTED MISMATCH", {
          inventoryItemId: item.id,
          actual: item.quantityCommitted,
          expected: recCommitted,
        });
      }

      // Push an updated shallow copy of the item
      result.push({ ...item });
      return result;
    }, []);

    // Then set your inventoryItems
    const updatedInventoryItems = {
      ...existingInvItems,
      items: filteredItems,
    };

    // 6) Filter out entire inventory if insufficient on-hand
    const shouldFilter =
      !isContainerShipment &&
      (recalculatedQuantityOnHand <= 0 ||
        (recalculatedCommittedTotal > 0 &&
          recalculatedQuantityOnHand <= recalculatedCommittedTotal) ||
        (actualCommitted > 0 && actualOnHand <= actualCommitted));

    if (shouldFilter) return accum;

    // 7) Push the updated inventory
    accum.push({
      ...inventory,
      quantityAvailable,
      inventoryItems: updatedInventoryItems,
    });
    return accum;
  }, []);
};

export const ShipmentItemEditActionButtons = (props) => {
  const classes = useStyles();
  const { record } = props;

  return (
    <div className={classes.actionButtons}>
      <ReorderButtons {...props} classes={classes} />
      {record.status === ShipmentLineItemStatus.NEW ? (
        <RemoveInventoryButton {...props} />
      ) : (
        <EditShipmentLineItemButton {...props} />
      )}
    </div>
  );
};

export const ShipmentItemCreateActionButtons = (props) => {
  const classes = useStyles();

  return (
    <div className={classes.actionButtons}>
      <ReorderButtons {...props} classes={classes} />
      <RemoveInventoryButton {...props} />
    </div>
  );
};

const RemoveInventoryButton = ({
  record,
  inventories,
  setInventories,
  ...props
}) => {
  const handleDelete = () => {
    const newInventories = inventories.items.filter(
      (itm) => itm.sortKey !== record.sortKey,
    );
    setInventories(newInventories);
  };

  return (
    <IconButtonWithTooltip label="Delete" size="small" onClick={handleDelete}>
      <DeleteIcon />
    </IconButtonWithTooltip>
  );
};

const EditShipmentLineItemButton = ({
  record,
  form,
  outbound,
  history,
  backPath,
  ...props
}) => {
  const toPath = record ? "/shipmentLineItems/" + record.id : "";
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleConfirmEdit = (event) => {
    event.stopPropagation();
    const formState = form.getState();
    debugger;

    if (formState.dirty) {
      setConfirmOpen(true);
    } else
      deepRedirect(history, toPath, backPath, {
        outbound: outbound,
      });
  };

  const handleConfirmClose = () => setConfirmOpen(false);
  const handleConfirmRedirect = () =>
    deepRedirect(history, toPath, backPath, {
      outbound: outbound,
    });

  return (
    <>
      <Button label="Edit" color="primary" onClick={handleConfirmEdit}>
        <EditIcon />
      </Button>
      {confirmOpen && (
        <Confirm
          isOpen={confirmOpen}
          loading={false}
          title="Un-saved Data Confirmation"
          content="You have un-saved changes. If you continue, all changes will be lost. Proceed?"
          onConfirm={handleConfirmRedirect}
          onClose={handleConfirmClose}
        />
      )}
    </>
  );
};

const ReorderButtons = ({
  record,
  inventories,
  setInventories,
  classes,
  ...props
}) => {
  const index =
    record.sortKey >= SORTKEY_BUFFER
      ? record.sortKey - SORTKEY_BUFFER
      : record.sortKey;
  const max = inventories.items.length;

  const handleReorder = (sourceIndex, destinationIndex) => {
    debugger;
    const reorderedValue = [...inventories.items];
    const [removed] = reorderedValue.splice(sourceIndex, 1);
    reorderedValue.splice(destinationIndex, 0, removed);
    reorderedValue.forEach((item, index) => {
      if (item) item.sortKey = index + SORTKEY_BUFFER;
    });
    setInventories(reorderedValue);
  };

  return (
    <div className={classes.reorderButtons}>
      <IconButtonWithTooltip
        label="ra.action.move_up"
        size="small"
        disabled={index <= 0}
        onClick={() => handleReorder(index, index - 1)}
      >
        <ArrowUpwardIcon />
      </IconButtonWithTooltip>
      <IconButtonWithTooltip
        label="ra.action.move_down"
        size="small"
        disabled={max == null || index >= max - 1}
        onClick={() => handleReorder(index, index + 1)}
      >
        <ArrowDownwardIcon />
      </IconButtonWithTooltip>
    </div>
  );
};

const RemoveItemButton = ({
  rowIndex,
  record,
  inventories,
  setInventories,
  ...props
}) => {
  const handleLocationRemove = (event) => {
    const newInventories = [];

    inventories.items.forEach((inv) => {
      if (inv.inventoryId === record.inventoryId) {
        const newInventoryItems = inv.inventoryItems.items.filter(
          (_, locIndex) => locIndex !== rowIndex,
        );
        const newTotal = newInventoryItems.reduce(
          (total, loc) => total + loc.quantityToPick,
          0,
        );
        if (newTotal !== 0)
          newInventories.push({
            ...inv,
            totalQuantityToPick: newTotal,
            inventoryItems: { items: newInventoryItems },
          });
      } else {
        newInventories.push(inv);
      }
    });
    setInventories(newInventories);
  };

  return (
    <Box mt={1.5}>
      <Button label="Remove" color="default" onClick={handleLocationRemove}>
        <RemoveIcon />
      </Button>
    </Box>
  );
};

export const InventoryItemsEditGridInput = ({
  record,
  inventories,
  setInventories,
  ...props
}) => {
  return record.status === ShipmentLineItemStatus.NEW ? (
    <InventoryItemsArrayInput
      {...props}
      record={record}
      inventories={inventories}
      setInventories={setInventories}
    />
  ) : (
    <InventoryItemsDataGrid {...props} record={record} />
  );
};

export const getQuantityToPickLabel = (shipmentLineItem) => {
  if (
    isBulkCommodity(shipmentLineItem.inventory?.reloadYardCommodityId) &&
    shipmentLineItem.status !== ShipmentLineItemStatus.SHIPPED
  ) {
    return "Est. Qty To Pick";
  }
  return "Qty. To Pick";
};

export const InventoryItemsArrayInput = ({
  record,
  inventories,
  setInventories,
  ...props
}) => {
  const classes = useStyles();
  const inventoryIdx = inventories.items.findIndex(
    (itm) => itm.id === record.id,
  );

  const handleQuantityChange = (event) => {
    debugger;
    const value = event.target.value;
    const locationIndex = parseInt(
      event.target.id.match(/\[(\d+)\](?=.*\[(\d+)\])/)[2],
    );
    const newInventories = inventories.items.map((inv) => {
      if (inv.inventoryId === record.inventoryId) {
        const newInventoryItems = [...inv.inventoryItems.items];
        newInventoryItems[locationIndex].quantityToPick = value
          ? parseFloat(value)
          : null;
        const newTotal = newInventoryItems.reduce((total, loc) => {
          return total + (Number(loc.quantityToPick) || 0);
        }, 0);

        return {
          ...inv,
          totalQuantityToPick: newTotal,
          inventoryItems: { items: newInventoryItems },
        };
      }
      return inv;
    });
    setInventories(newInventories);
  };

  const getSource = (idx, source) =>
    `shipmentLineItems.items[${inventoryIdx}].inventoryItems.items[${idx}].${source}`;

  const calcMaxQtyOutbound = (item) => {
    return (
      item.inventoryItem.quantityOnHand -
      Number(item.inventoryItem.quantityCommitted)
    );
  };

  return (
    <Paper variant="outlined" className={classes.paper}>
      {record.inventoryItems?.items?.map((item, idx) => (
        <Grid container direction="row" spacing={1} key={item.id ?? idx}>
          <Grid item xs={3}>
            <TextInput
              source={getSource(idx, "inventoryItem.locationFullName")}
              record={item}
              label="Location"
              variant="outlined"
              fullWidth
              disabled
              helperText={false}
            />
          </Grid>
          <Grid item xs={3}>
            <NumberInput
              source={getSource(idx, "inventoryItem.quantityOnHand")}
              record={item}
              label="Qty. On Hand"
              variant="outlined"
              fullWidth
              disabled
              helperText={false}
            />
          </Grid>
          <Grid item xs={3}>
            <NumberInput
              source={getSource(idx, "quantityToPick")}
              record={item}
              label={getQuantityToPickLabel(record)}
              variant="outlined"
              fullWidth
              min="0"
              max={calcMaxQtyOutbound(item)}
              onChange={handleQuantityChange}
              validate={validateQuantity(0, calcMaxQtyOutbound(item))}
              helperText={false}
            />
          </Grid>
          <Grid item xs={2}>
            <RemoveItemButton
              record={record}
              rowIndex={idx}
              inventories={inventories}
              setInventories={setInventories}
            />
          </Grid>
        </Grid>
      ))}
    </Paper>
  );
};

export const InventoryItemsDataGrid = ({ record, ...props }) => {
  const classes = useStyles();
  const defaultPerPage = 10;
  const defaultSort = { field: "locationFullName", order: "ASC" };
  const inventoryItems = record?.inventoryItems?.items || [];

  const listContext = useList({
    ...props,
    data: inventoryItems,
    ids: inventoryItems.map(({ id }) => id),
    loaded: true,
    sort: defaultSort,
    perPage: defaultPerPage,
    basePath: "/inventoryItems",
    resource: "inventoryItems",
  });

  return (
    <Paper variant="outlined" className={classes.paper}>
      <ListContextProvider value={listContext}>
        <Datagrid rowClick={false} hasBulkActions={false}>
          <FunctionField
            source="inventoryItem.locationFullName"
            label="Location"
            render={(record) => (
              <>
                {record?.inventoryItem?.locationFullName}
                {(record?.hasException || record?.barcodeException) && (
                  <ExceptionIcon fontSize="small" className={classes.icons} />
                )}
              </>
            )}
          />
          <NumberField
            source="inventoryItem.quantityOnHand"
            label="Qty. On Hand"
          />
          <NumberField
            source="inventoryItem.quantityCommitted"
            label="Qty. Committed"
          />
          <NumberField source="quantityToPick" label="Qty. To Pick" />,
          <NumberField source="quantityReturned" label="Qty. Returned" />,
          <FunctionField
            source="netQuantity"
            label="Net Qty"
            sortable={false}
            render={(record) => {
              const picked = Number(record?.quantityToPick || 0);
              const returned = Number(record?.quantityReturned || 0);
              return trimDecimal(Math.max(0, picked - returned), 3);
            }}
          />
          ,
          <TextField source="loadedBy" label="Loaded By" />,
        </Datagrid>
      </ListContextProvider>
    </Paper>
  );
};

export const calcTotalQuantity = (formData) => {
  const pickList = formData.shipmentLineItems?.items;
  let total = 0;
  if (pickList) {
    pickList.forEach((item) => {
      const shippedQty = Number(item?.totalQuantityToPick || 0);
      const returnedQtyFromLine = Number(item?.totalQuantityReturned || 0);
      const returnedQtyFromItems = (item?.inventoryItems?.items || []).reduce(
        (sum, invItem) => sum + Number(invItem?.quantityReturned || 0),
        0,
      );
      const returnedQty = returnedQtyFromLine || returnedQtyFromItems || 0;
      total += Math.max(0, shippedQty - returnedQty);
    });
  }

  return total;
};

export const calcQtyAvailable = (scopedFormData) => {
  if (!scopedFormData.inventory) return 0;
  const qtyAvail =
    scopedFormData.inventory && scopedFormData.quantityCommitted
      ? scopedFormData.inventory.quantityOnHand -
        scopedFormData.quantityCommitted
      : scopedFormData.inventory.quantityOnHand -
        scopedFormData.inventory.quantityCommitted;
  return qtyAvail;
};

/**
 * Single weight unit for line items: inventory.weightUOM or product.defaultWeightUOM.
 * Returns when all items share the same unit, or first item's unit as fallback.
 */
const getInventoryWeightUOM = (lineItems) => {
  if (!lineItems?.length) return null;
  const unitsFromItems = lineItems.map(
    (itm) =>
      itm.inventory?.weightUOM || itm.inventory?.product?.defaultWeightUOM,
  );
  const unitsSet = new Set(unitsFromItems.filter(Boolean));
  if (unitsSet.size === 1) return unitsSet.values().next().value;
  return unitsFromItems.find(Boolean) ?? null;
};

/** Prefer primary weight units, else infer from line items. */
const resolveWeightUOM = (primaryUnits, lineItems) =>
  primaryUnits || getInventoryWeightUOM(lineItems);

/**
 * Resolve and validate weight units from pick list; sets setWeightError on mixed/missing.
 */
const getWeightUOM = (outbound, setWeightError) => {
  const pickList = outbound.shipmentLineItems?.items;
  setWeightError && setWeightError(null);
  const weightUOM = getInventoryWeightUOM(pickList);
  if (!pickList?.length) return weightUOM;
  let hasBlanks = false;
  let mixed = false;
  for (const item of pickList) {
    const u =
      item.inventory?.weightUOM || item.inventory?.product?.defaultWeightUOM;
    if (!u || !item.inventory?.weight) hasBlanks = true;
    else if (weightUOM && u !== weightUOM) mixed = true;
  }
  if (mixed) {
    setWeightError && setWeightError("Mixed weight UOM in inventories.");
    return null;
  }
  if (weightUOM && hasBlanks) {
    setWeightError && setWeightError("Missing weight in inventories.");
    return null;
  }
  return weightUOM;
};

/**
 * Resolve weight per unit for one line item's inventory in target units (toUnits).
 * Uses inventory.weight (with unit check/conversion) then product weight columns.
 * @returns { number | null } weight in toUnits, or null if unavailable
 */
const getLineItemWeightInUnits = (lineItem, toUnits) => {
  const inventory = lineItem?.inventory;
  if (!toUnits || !inventory) return null;
  const key = toUnits.toLowerCase();
  const inventoryWeight = inventory?.weight;
  const inventoryWeightUOM = inventory?.weightUOM;
  const product = inventory?.product;

  if (
    inventoryWeight != null &&
    inventoryWeight !== "" &&
    inventoryWeightUOM === toUnits
  ) {
    return Number(inventoryWeight);
  }
  if (
    inventoryWeight != null &&
    inventoryWeight !== "" &&
    inventoryWeightUOM &&
    inventoryWeightUOM !== toUnits &&
    product
  ) {
    const conv = convertUnits(
      inventoryWeight,
      inventoryWeightUOM,
      toUnits,
      product,
    );
    if (!conv.error && conv.value != null) return conv.value;
  }
  const productWeight = product?.[key];
  if (productWeight != null && productWeight !== "") {
    return Number(productWeight);
  }
  return null;
};

/**
 * Single place for net weight from shipment line items.
 * Options: { totalUOM, convert, setWeightError, weightUOMLabel }.
 * When convert is true, uses convertUnits(totalQuantityToPick, totalUOM, toUnits, product) per line.
 * Otherwise uses getLineItemWeightInUnits (inventory.weight + product weight columns).
 */
const computeNetWeightFromLineItems = (lineItems, toUnits, options = {}) => {
  const {
    totalUOM,
    convert,
    setWeightError,
    weightUOMLabel = toUnits,
  } = options;
  let total = 0;
  let error = null;
  let hasWeights = true;

  if (!toUnits || !lineItems?.length) {
    return { total: null, error: null, hasWeights: false };
  }

  for (const lineItem of lineItems) {
    const inventory = lineItem?.inventory;
    let weight = 0;
    let weightResolved = false;
    if (convert && totalUOM != null) {
      const unitValue = convertUnits(
        lineItem.totalQuantityToPick,
        totalUOM,
        toUnits,
        inventory?.product,
      );
      weight = unitValue.value;
      weightResolved = !unitValue.error && unitValue.value != null;
      if (!error && unitValue.error) {
        error = inventory?.product
          ? `No conversion for ${weightUOMLabel}`
          : `Product missing entry for ${weightUOMLabel}`;
      }
    } else {
      const resolved = getLineItemWeightInUnits(lineItem, toUnits);
      weight = resolved != null ? resolved : 0;
      weightResolved = resolved != null;
    }
    const qty = Number(lineItem?.totalQuantityToPick) ?? 0;
    hasWeights = hasWeights && weightResolved;
    if (hasWeights) {
      total +=
        (Number(weight) || 0) *
        (convert ? (lineItem.totalQuantityToPick ?? 0) : qty);
    } else if (!error) {
      error = "Missing weight in inventories.";
    }
  }

  if (error && setWeightError) setWeightError(error);
  return {
    total: hasWeights && total >= 0 ? trimDecimal(total) : null,
    error,
    hasWeights,
  };
};

/**
 * Compute total net weight from outbound shipmentLineItems (inventory.weight or product weight columns).
 * Uses outbound.weightUnits, else getInventoryWeightUOM (inv.weightUOM or product.defaultWeightUOM).
 */
export const getTotalNetWeight = (outbound) => {
  const lineItems = outbound?.shipmentLineItems?.items;
  if (!lineItems?.length) return { total: null, weightUOM: null };
  const weightUOM = resolveWeightUOM(outbound?.weightUnits, lineItems);
  if (!weightUOM) return { total: null, weightUOM: null };
  const { total, error } = computeNetWeightFromLineItems(lineItems, weightUOM, {
    convert: false,
  });
  return {
    total: error ? null : total,
    weightUOM,
  };
};

/**
 * Render string for total net weight (e.g. "1,234 lbs"). Uses getTotalNetWeight.
 */
export const renderTotalNetWeight = (record) => {
  const { total, weightUOM } = getTotalNetWeight(record);
  if (total == null || total < 0) return "";
  return `${Number(total).toLocaleString()} ${getUnitName(weightUOM)}`.trim();
};

export const calcNetWeight = (outbound, setWeightError, weightUOM) => {
  const lineItems = outbound.shipmentLineItems?.items;
  const inventoryWeightUOM = getInventoryWeightUOM(lineItems);
  const toUnits = weightUOM || outbound.weightUnits || inventoryWeightUOM;
  const convert =
    toUnits &&
    (outbound.weightUnits
      ? toUnits !== outbound.weightUnits
      : toUnits !== inventoryWeightUOM);
  if (setWeightError) setWeightError(null);

  try {
    const { total, error, hasWeights } = computeNetWeightFromLineItems(
      lineItems,
      toUnits,
      {
        totalUOM: outbound.totalUOM,
        convert,
        setWeightError,
        weightUOMLabel: weightUOM,
      },
    );
    if (error) return null;
    if (hasWeights && total != null) return total;
    return outbound.netWeight;
  } catch (err) {
    logError("outboundUtils:calcNetWeight:", err, false);
    return null;
  }
};

/**
 * Default POS email subject for a shipment, from the account template.
 * Used to prefill the Send dialog's Subject field.
 */
export const getPOSEmailSubject = (record, account) =>
  generateEmailSubject(
    (account || record?.account)?.posSubject || POSDefaultSubject,
    record,
  );

export const emailPOS = async (
  record,
  toEmail,
  ccEmail,
  enclosePhotos,
  encloseDocs,
  notify,
  operatingAccount,
  subjectOverride,
) => {
  if (!toEmail) return false;
  const subject =
    subjectOverride || getPOSEmailSubject(record, operatingAccount);

  try {
    const htmlDoc = getOutboundPOSBody(record, enclosePhotos, encloseDocs);

    const emailBody = {
      from: "no_reply transloads.co <pos@transloads.co>",
      to: splitEmails(toEmail),
      cc: splitEmails(ccEmail),
      subject: subject,
      template: "emailTemplateWide.html",
      htmlContent: htmlDoc,
      attachPDF: true,
    };
    sendEmail(emailBody, "outboundShipment:outboundUtils:emailPOS", notify);
    return true;
  } catch (error) {
    logError("outboundShipment:outboundUtils:emailPOS", error, notify);
    return false;
  }
};

export const getInfoBubble = (formData, scopedFormData) => {
  const shippedQty = scopedFormData?.totalQuantityToPick || 1;
  const inventory = scopedFormData?.inventory;
  const product = inventory?.product;

  // Get commodity for determining calculation type
  const commodity =
    inventory?.reloadYardCommodityId ||
    formData?.reloadYardCommodityId ||
    inventory?.reloadYardCommodity?.commodity ||
    formData?.reloadYardCommodity?.commodity;

  const infoBubble = getUnitConversions({
    inventory,
    quantity: shippedQty,
    fromUnits: formData.totalUOM,
    commodity,
    product,
  });

  // Build tooltip content
  const hasContent = infoBubble.length > 0;

  // Show broken link icon if no product
  const showBrokenLink = !product;

  // Build tooltip title
  const tooltipTitle = (
    <>
      {product && (
        <Typography>Product Code: {inventory?.productCode}</Typography>
      )}
      {infoBubble.length > 0 && (
        <>
          <Typography>
            {shippedQty.toLocaleString()} {getUnitName(formData.totalUOM)} =
          </Typography>
          {infoBubble.map((itm) =>
            itm.value ? (
              <Typography key={itm.unit}>
                &emsp;{trimDecimal(itm.value).toLocaleString()}{" "}
                {getUnitName(itm.unit)}
              </Typography>
            ) : null,
          )}
        </>
      )}
    </>
  );

  return (
    <>
      &nbsp;
      {showBrokenLink && <ProductLinkIcon />}
      {hasContent && (
        <Tooltip title={tooltipTitle} placement="right">
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      )}
    </>
  );
};

export const setupAccountDetails = (
  fromAccount,
  operatingAccount,
  transferToAcct,
  form,
  setOperatingAccount,
) => {
  // fromAccount type  operatingAccount  transferToAcct  billToAccountId
  // -----------------------------------------------------------------------
  // INTERNAL          (user picks)                      operatingAccount.id
  // INTERNAL                            Acme            fromAccount.id
  // BROKERED                                            fromAccount.defaultBillToAccountId
  // CUSTOMER                                            fromAccount.id
  // -----------------------------------------------------------------------

  let sourceAccount = null;

  if (isInternalAccount(fromAccount)) {
    // INTERNAL: sourceAccount = selected billing/operating account, or fromAccount as placeholder
    sourceAccount = transferToAcct
      ? fromAccount
      : operatingAccount || fromAccount;
  } else {
    sourceAccount = fromAccount;
  }

  if (setOperatingAccount) {
    setOperatingAccount(sourceAccount);
  }

  // BROKERED: billing goes to the account's defaultBillToAccountId (the broker), not to
  // the operating account (sourceAccount). For all other types billToAccountId = sourceAccount.
  const billToId =
    isBrokeredAccount(fromAccount) && !transferToAcct
      ? fromAccount.defaultBillToAccountId || sourceAccount?.id || ""
      : sourceAccount?.id || "";
  form.change("billToAccountId", billToId);

  form.change("instructions", sourceAccount?.outboundInstructions);
  form.change("mustTarp", sourceAccount?.mustTarp || false);
  form.change("skipSignature", sourceAccount?.outboundSkipSignature || false);
};

export const getManualChargeAmount = (rule, scopedFormData) => {
  const billingUOM = rule?.billingUOM || "";
  const quantity = Number(scopedFormData?.quantity ?? 0);

  if (!rule || !billingUOM) return false;
  const rate = getUnitPrice(rule.service, billingUOM, null);
  if (rate == null || !quantity || quantity <= 0) return false;
  const charge = formatCurrency(quantity * rate, 2);

  return charge != null ? `Charge: ${charge}` : false;
};

export const applyManualRuleDefaultQuantity = ({
  value,
  applicableRules,
  formData,
  scopedFormData,
  form,
  getSource,
}) => {
  if (!value) return;
  const ruleId = value?.target?.value || value;
  const rule = applicableRules.find((r) => r.id === ruleId);
  if (!rule) return;

  const currentQty = scopedFormData?.quantity;
  const totalQuantity = calcTotalQuantity(formData);
  let nextQty = currentQty;

  if (!currentQty || currentQty <= 0) {
    if (
      (rule.event === ChargeEvent.STORAGE &&
        (rule.frequency === StorageFrequency.ON_RELEASE ||
          rule.frequency === StorageFrequency.ON_PULL ||
          rule.frequency === StorageFrequency.ON_DEPLETION)) ||
      rule.billingUOM === Unit.CAR ||
      rule.billingUOM === Unit.TL
    ) {
      nextQty = 1;
    } else if (
      Number(totalQuantity) > 0 &&
      rule.billingUOM === formData?.totalUOM
    ) {
      nextQty = totalQuantity;
    }
  }

  if (nextQty != null && nextQty !== currentQty) {
    form.change(getSource("quantity"), nextQty);
  }
};

export const buildManualChargeOps = (
  shipment,
  recordCharges = [],
  formCharges = null,
) => {
  const now = new Date().toISOString();
  const incoming = formCharges || [];

  const createList = [];
  const updateList = [];
  const deleteList = [];
  const seen = new Set();

  incoming.forEach((mc, idx) => {
    const chargeRuleId = mc?.chargeRuleId || mc?.chargeRule?.id;
    const quantity = Number(mc?.quantity ?? 0);
    if (!chargeRuleId || !quantity || quantity <= 0) return;

    // Only MANUAL execution rules should be persisted here
    if (mc?.execution && mc.execution !== BillingAutomation.MANUAL) return;

    const base = {
      id: mc.id || uuidv4(),
      owner: shipment.owner,
      groups: shipment.groups,
      reloadYardId: shipment.reloadYardId,
      parentId: shipment.id,
      parentType: ResourceType.OUTBOUND,
      chargeRuleId,
      quantity,
      sortKey: mc.sortKey ?? idx + 1,
      invoiceServiceId: mc.invoiceServiceId || null,
      updatedAt: now,
    };

    if (mc.id) {
      updateList.push(base);
      seen.add(mc.id);
    } else {
      createList.push({ ...base, createdAt: now });
    }
  });

  (recordCharges || []).forEach((mc) => {
    if (!mc?.id) return;
    if (!seen.has(mc.id)) deleteList.push({ id: mc.id });
  });

  return { createList, updateList, deleteList };
};

export const setQtyAvailable = (event, form, getSource, scopedFormData) => {
  const value = Number(event.target.value);
  const initQtyCommitted = scopedFormData.inventory.quantityCommitted;
  const initQtyOutbound = form.getFieldState(
    getSource("totalQuantityToPick"),
  )?.initial;
  let qtyComm = 0;
  debugger;

  if (
    typeof initQtyOutbound === "undefined" ||
    scopedFormData.status === ShipmentLineItemStatus.NEW
  )
    qtyComm = initQtyCommitted + value;
  else qtyComm = trimDecimal(initQtyCommitted - initQtyOutbound + value, 3);

  form.change(getSource("quantityCommitted"), qtyComm);
  form.change(
    getSource("quantityAvailable"),
    trimDecimal(scopedFormData.inventory.quantityOnHand - qtyComm, 3),
  );
};

export const handlePickListWeight = (form, setWeightError) => {
  const formState = form.getState();
  const formData = formState.values;
  const weightUOM = getWeightUOM(formData, setWeightError);
  let totalWeight = null;
  if (weightUOM) {
    totalWeight = calcNetWeight(formData, setWeightError, weightUOM);
    form.change("weightUnits", weightUOM);
    form.change("netWeight", totalWeight);
  } else {
    form.change("weightUnits", null);
    form.change("netWeight", null);
  }
};

export const hasAllProducts = (form, outbound) => {
  let retVal = true;

  if (outbound?.shipmentLineItems?.items) {
    retVal = !outbound.shipmentLineItems.items.some(
      (item) => !item.inventory.product,
    );
  } else {
    const fieldState = form.getFieldState("shipmentLineItems.items");
    const lineItems = fieldState?.value;
    retVal = !lineItems?.some((item) => !item.inventory?.product);
  }

  return retVal;
};

export const updateLambdaInventory = (
  inventory,
  pickListItem,
  isAdvance,
  updateDateTime,
  quantityToPickOrig,
) => {
  debugger;
  const toUpdateInventory = { ...inventory };

  if (isAdvance && pickListItem.status === ShipmentLineItemStatus.SHIPPED) {
    // record advanced to SHIPPED
    const qtyCommitedOrig = quantityToPickOrig
      ? quantityToPickOrig
      : pickListItem.totalQuantityToPick;
    toUpdateInventory.quantityOnHand = trimDecimal(
      toUpdateInventory.quantityOnHand - qtyCommitedOrig,
      3,
    );
    toUpdateInventory.quantityCommitted = trimDecimal(
      toUpdateInventory.quantityCommitted - qtyCommitedOrig,
      3,
    );
    const note = {
      type: NoteType.SHIPMENT,
      date: updateDateTime,
      by: window.appUser?.fullName,
      text: `Shipped Qty. of ${pickListItem.totalQuantityToPick}`,
    };
    toUpdateInventory.history = toUpdateInventory.history
      ? [
          ...(Array.isArray(toUpdateInventory.history)
            ? toUpdateInventory.history
            : [toUpdateInventory.history]),
          note,
        ]
      : [note];
  }

  if (!isBulkArea(toUpdateInventory.areaId)) {
    // aggregated inventories are managed in myYard > Locations
    toUpdateInventory.status =
      toUpdateInventory.quantityOnHand <= 0
        ? InventoryStatus.SHIPPED
        : toUpdateInventory.status;
  }
  if (toUpdateInventory.status === InventoryStatus.SHIPPED) {
    toUpdateInventory.depletedAt = updateDateTime;
    toUpdateInventory.lastOutboundId = pickListItem.outboundShipmentId;
    toUpdateInventory.turnDays = differenceInDays(
      new Date(updateDateTime),
      new Date(toUpdateInventory.receivedAt),
    );
  }

  toUpdateInventory.updatedAt = updateDateTime;

  // toUpdateInventory["status#lotNumber"] =  `${toUpdateInventory.status}#${toUpdateInventory.lotNumber}`;
  // toUpdateInventory["status#accountName#productName#lotNumber"] = `${toUpdateInventory.status}#${toUpdateInventory.accountName}#${toUpdateInventory.productName}#${toUpdateInventory.lotNumber}`;
  // toUpdateInventory["status#accountId#productName#lotNumber"] = `${toUpdateInventory.status}#${toUpdateInventory.accountId}#${toUpdateInventory.productName}#${toUpdateInventory.lotNumber}`;

  delete toUpdateInventory.account;
  delete toUpdateInventory.location;
  delete toUpdateInventory.inboundShipment;
  delete toUpdateInventory.inventoryItems;
  delete toUpdateInventory.product;
  delete toUpdateInventory.reloadYardCommodity;
  delete toUpdateInventory.shipmentLineItems;

  return toUpdateInventory;
};

export const createLambdaShipmentLineItem = (
  outbound,
  lineItem,
  updateDateTime,
  isTransfer,
) => {
  const shipmentLineItemInput = {
    id: lineItem.lineItemUuid || uuidv4(), // stable id assigned at add-time; retries re-Put the same row
    __typename: "ShipmentLineItem",
    createdAt: updateDateTime,
    updatedAt: updateDateTime,
    active: outbound.active,
    owner: outbound.owner,
    groups: outbound.groups,
    lotNumber: lineItem.lotNumber,
    poNumber: outbound.poNumber,
    totalQuantityToPick: lineItem.totalQuantityToPick,
    outboundShipmentId: outbound.id,
    reloadYardId: outbound.reloadYardId,
    inventoryId: lineItem.inventoryId,
    accountId: outbound.accountId,
    history: isTransfer
      ? [
          {
            type: NoteType.INVENTORY,
            date: updateDateTime,
            by: window.appUser?.fullName,
            text: `Transfer of ${lineItem.totalQuantityToPick} ${lineItem.inventory.inventoryUOM} from ${outbound.accountName} to ${outbound.transferToAccountName}`,
          },
        ]
      : [
          {
            type: NoteType.INVENTORY,
            date: updateDateTime,
            by: window.appUser?.fullName,
            text: `Committed Qty. of ${lineItem.totalQuantityToPick} ${lineItem.inventory.inventoryUOM}`,
          },
        ],
    sortKey: lineItem.sortKey,
    // sortKey: index + SORTKEY_BUFFER,
  };

  if (
    outbound.status === OutboundStatus.PENDING ||
    outbound.status.startsWith(OutboundStatus.ADVICE_ALL)
  )
    shipmentLineItemInput.status = ShipmentLineItemStatus.PENDING;
  else if (
    outbound.status === OutboundStatus.ARRIVED ||
    outbound.status === OutboundStatus.PLACED
  )
    shipmentLineItemInput.status = ShipmentLineItemStatus.PENDING;
  else if (outbound.status === OutboundStatus.LOADED)
    shipmentLineItemInput.status = ShipmentLineItemStatus.LOADED;
  else if (outbound.status === OutboundStatus.RELEASED)
    shipmentLineItemInput.status = ShipmentLineItemStatus.SHIPPED;

  return shipmentLineItemInput;
};

const updateLambdaShipmentLineItem = (outbound, lineItem, updateDateTime) => {
  const toUpdateLineItem = {
    id: lineItem.id,
    history: lineItem.history,
    poNumber: outbound.poNumber,
    inventoryId: lineItem.inventoryId,
    totalQuantityToPick: lineItem.totalQuantityToPick,
    sortKey: lineItem.sortKey,
    // sortKey: index + SORTKEY_BUFFER,
    status: lineItem.status,
    outboundShipmentId: lineItem.outboundShipmentId,
    updatedAt: updateDateTime,
  };

  if (outbound.status === OutboundStatus.PENDING)
    toUpdateLineItem.status = ShipmentLineItemStatus.PENDING;
  else if (
    outbound.status === OutboundStatus.ARRIVED ||
    outbound.status === OutboundStatus.PLACED
  )
    toUpdateLineItem.status = ShipmentLineItemStatus.PENDING;
  else if (outbound.status === OutboundStatus.LOADED)
    toUpdateLineItem.status = ShipmentLineItemStatus.LOADED;
  else if (outbound.status === OutboundStatus.RELEASED)
    toUpdateLineItem.status = ShipmentLineItemStatus.SHIPPED;

  return toUpdateLineItem;
};

export const createLambdaShipmentLineItemInventoryItem = (
  shipmentLineItem,
  inventoryItem,
  updateDateTime,
) => {
  const inventoryItemInput = {
    id: inventoryItem.sliiiUuid || uuidv4(), // stable id assigned at add-time; retries re-Put the same row
    __typename: "ShipmentLineItemInventoryItem",
    createdAt: updateDateTime,
    updatedAt: updateDateTime,
    owner: shipmentLineItem.owner,
    groups: shipmentLineItem.groups,
    reloadYardId: shipmentLineItem.reloadYardId,
    outboundShipmentId: shipmentLineItem.outboundShipmentId,
    shipmentLineItemId: shipmentLineItem.id,
    inventoryId: shipmentLineItem.inventoryId,
    inventoryItemId: inventoryItem.inventoryItemId,
    quantityToPick: inventoryItem.quantityToPick,
    status: shipmentLineItem.status,
  };

  return inventoryItemInput;
};

export const updateLambdaShipmentLineItemInventoryItem = (
  shipmentLineItem,
  shipmentLineItemInventoryItem,
  updateDateTime,
) => {
  shipmentLineItemInventoryItem.status = shipmentLineItem.status;

  const shipmentLineItemInventoryItemInput = {
    ...shipmentLineItemInventoryItem,
    updatedAt: updateDateTime,
    status: shipmentLineItem.status,
  };

  delete shipmentLineItemInventoryItemInput.groups;
  delete shipmentLineItemInventoryItemInput.inventoryItem;

  return shipmentLineItemInventoryItemInput;
};

export const createLambdaInventoryItem = (
  outbound,
  lineIteminvItem,
  inventory,
  updateDateTime,
) => {
  const inventoryItemInput = {
    ...lineIteminvItem.inventoryItem,
    id: uuidv4(),
    __typename: "InventoryItem",
    groups: inventory.groups,
    createdAt: updateDateTime,
    updatedAt: updateDateTime,
    quantityOnHand: lineIteminvItem.quantityToPick,
    quantityCommitted: 0,
    status: InventoryStatus.STORED,
    inboundShipmentId: inventory.inboundShipmentId,
    inventoryId: inventory.id,
    accountId: inventory.accountId,
    reloadYardId: outbound.reloadYardId,
    history: [
      {
        type: NoteType.INVENTORY,
        date: updateDateTime,
        by: window.appUser?.fullName,
        text: `Transfer of ${lineIteminvItem.quantityToPick} ${inventory.inventoryUOM} from ${outbound.accountName} to ${outbound.transferToAccountName}`,
      },
    ],
  };

  // sanitize inventoryItem props not relevant to trainsfer inventory.
  delete inventoryItemInput.depletedAt;
  delete inventoryItemInput.location;

  return inventoryItemInput;
};

export const updateLambdaInventoryItem = (
  outbound,
  lineIteminvItem,
  updateMode,
  updateDateTime,
  quantityToPickOrig,
) => {
  const toUpdateInventoryItem = {
    ...lineIteminvItem.inventoryItem,
    updatedAt: updateDateTime,
  };
  debugger;

  if (updateMode === "create") {
    // InventoryItme adde4d to new shipmentLineItem, lineIteminvItem
    toUpdateInventoryItem.quantityCommitted = trimDecimal(
      toUpdateInventoryItem.quantityCommitted + lineIteminvItem.quantityToPick,
      3,
    );
  } else if (updateMode === "transfer") {
    const historyNote = {
      type: NoteType.INVENTORY,
      date: updateDateTime,
      by: window.appUser?.fullName,
      text: `Transfer of ${lineIteminvItem.quantityToPick} ${lineIteminvItem.inventory?.inventoryUOM} from ${outbound.accountName} to ${outbound.transferToAccountName}`,
    };
    toUpdateInventoryItem.quantityOnHand = trimDecimal(
      toUpdateInventoryItem.quantityOnHand - lineIteminvItem.quantityToPick,
      3,
    );
    if (!isBulkLocation(toUpdateInventoryItem.location)) {
      // aggregated inventories are managed in myYard > Locations
      toUpdateInventoryItem.status =
        toUpdateInventoryItem.quantityOnHand <= 0
          ? InventoryStatus.SHIPPED
          : toUpdateInventoryItem.status;
    }
    if (toUpdateInventoryItem.status === InventoryStatus.SHIPPED) {
      toUpdateInventoryItem.depletedAt = updateDateTime;
    }
    toUpdateInventoryItem.history = [
      ...toUpdateInventoryItem.history,
      historyNote,
    ];
  } else if (
    updateMode === "advance" &&
    lineIteminvItem.status === ShipmentLineItemStatus.SHIPPED
  ) {
    toUpdateInventoryItem.quantityOnHand = trimDecimal(
      toUpdateInventoryItem.quantityOnHand - lineIteminvItem.quantityToPick,
      3,
    );
    const qtyCommitedOrig = quantityToPickOrig
      ? quantityToPickOrig
      : lineIteminvItem.quantityToPick;
    toUpdateInventoryItem.quantityCommitted = trimDecimal(
      toUpdateInventoryItem.quantityCommitted - qtyCommitedOrig,
      3,
    );
    if (!isBulkLocation(toUpdateInventoryItem.location)) {
      // aggregated inventories are managed in myYard > Locations
      toUpdateInventoryItem.status =
        toUpdateInventoryItem.quantityOnHand <= 0
          ? InventoryStatus.SHIPPED
          : toUpdateInventoryItem.status;
    }
    if (toUpdateInventoryItem.status === InventoryStatus.SHIPPED) {
      toUpdateInventoryItem.depletedAt = updateDateTime;
    }
  } // no need to handle rollback becuase quantityOnHand/quantityCommitted not changed until RELEASED

  delete toUpdateInventoryItem.area;
  delete toUpdateInventoryItem.groups;
  delete toUpdateInventoryItem.inventory;
  delete toUpdateInventoryItem.location;
  delete toUpdateInventoryItem.zone;
  return toUpdateInventoryItem;
};

const resetLambdaCapacityUsed = (
  location,
  shipmentLineItem,
  lineIteminvItem,
  updateDateTime,
) => {
  const inventory = shipmentLineItem.inventory;
  const inventoryItem = lineIteminvItem.inventoryItem;
  const previousStatus = shipmentLineItem.status;
  const newStatus = inventoryItem.status;
  const toUpdateLocation = { ...location, updatedAt: updateDateTime };
  debugger;

  if (
    newStatus === InventoryStatus.STORED &&
    (previousStatus === ShipmentLineItemStatus.LOADED ||
      previousStatus === ShipmentLineItemStatus.VERIFIED)
  ) {
    // on rollback if inventoryItem is UNLOADED / STORED update capacityUsed
    resetCapacityUsed(inventory, lineIteminvItem, toUpdateLocation);
  }

  return toUpdateLocation;
};

const resetCapacityUsed = (inventory, lineIteminvItem, location) => {
  // on rollback update the passed in location object
  const inventoryLoadType =
    inventory.reloadYardCommodity.commodity.locationLoadType;
  const locationLoadType = location.locationLoad[inventoryLoadType];
  const isOtherLoadType = location.locationLoad?.other.selected;
  debugger;

  if (location.type === LocationType.BULK) {
    location.capacityUsed =
      location.capacityUsed + lineIteminvItem.quantityToPick;
    // BULK productId reset done in LOCATIONS
  } else if (location.type === LocationType.STRUCTURED && !isOtherLoadType) {
    location.capacityUsed = location.capacityUsed + locationLoadType.capacity;
    if (!location.productId && inventory.productId)
      location.productId = inventory.productId;
  }
};

export const createLambdaInboundTransfer = (
  outbound,
  toAccount,
  lotTypes,
  lotNumbers,
  yardCommodities,
  areaId,
  zoneId,
  updateDateTime,
) => {
  const inboundCreateInput = {
    id: uuidv4(),
    active: "true",
    groups: outbound.groups,
    owner: outbound.owner,
    status: InboundStatus.RELEASED,
    state: InboundState.COMPLETED,
    createdAt: updateDateTime,
    updatedAt: updateDateTime,
    accountName: outbound.transferToAccountName,
    accountId: toAccount.id,
    billToAccountId: toAccount.defaultBillToAccountId || toAccount.id,
    shipFrom: outbound.accountName,
    poNumber: outbound.consigneePoNumber || outbound.poNumber || "TRANSFER",
    normalizedPoNumber: normalizeValue(outbound.poNumber),
    lotType: lotTypes.size === 1 ? [...lotTypes][0] : LotType.CARLOT,
    lotNumber: lotNumbers.size === 1 ? [...lotNumbers][0] : "TRANSFER",
    reloadYardCommodityId: [...yardCommodities][0],
    carrierType: CarrierType.TRUCK,
    carrierName: "TRANSFER",
    railCarNumber: "TRANSFER",
    lotNumberOverride: true,
    secondaryReview: false,
    skipInvoice: false,
    stockImport: false,
    returnShipment: false,
    containerDrop: false,
    sit: false,
    createDateTime: updateDateTime,
    scheduledDateTime: updateDateTime,
    notifyDateTime: updateDateTime,
    placedDateTime: updateDateTime,
    releaseDateTime: updateDateTime,
    totalUOM: outbound.totalUOM,
    reloadYardId: outbound.reloadYardId,
    areaId: areaId,
    zoneId: zoneId,
    history: outbound.history,
  };
  if (outbound.photos) inboundCreateInput.photos = outbound.photos;
  if (outbound.attachments)
    inboundCreateInput.attachments = outbound.attachments;

  return inboundCreateInput;
};

export const createLambdaInventoryTransfer = (
  outbound,
  inbound,
  lineItem,
  index,
  updateDateTime,
) => {
  const historyNote = {
    type: NoteType.INVENTORY,
    date: updateDateTime,
    by: window.appUser?.fullName,
    text: `Transfer of ${lineItem.totalQuantityToPick} ${lineItem.inventory.inventoryUOM} from ${outbound.accountName} to ${inbound.accountName}`,
  };

  const createInventoryInput = {
    ...lineItem.inventory,
    id: uuidv4(),
    __typename: "Inventory",
    owner: outbound.owner,
    createdAt: updateDateTime,
    updatedAt: updateDateTime,
    inboundShipmentId: inbound.id,
    poNumber: outbound.poNumber,
    accountName: inbound.accountName,
    accountId: inbound.accountId,
    productId: lineItem.inventory.productId ? lineItem.inventory.productId : "",
    quantityAdvised: lineItem.totalQuantityToPick,
    quantityReceived: lineItem.totalQuantityToPick,
    quantityOnHand: lineItem.totalQuantityToPick,
    quantityPhysical: lineItem.totalQuantityToPick,
    quantityCommitted: 0,
    receivedAt: updateDateTime,
    history: [historyNote],
    sortKey: index + SORTKEY_BUFFER,
  };

  delete createInventoryInput.depletedAt;
  delete createInventoryInput.lastOutboundId;
  delete createInventoryInput.productCode;
  delete createInventoryInput.productId;
  delete createInventoryInput.verificationRequired;

  delete createInventoryInput.account;
  delete createInventoryInput.area;
  delete createInventoryInput.inboundShipment;
  delete createInventoryInput.inventoryItems;
  delete createInventoryInput.location;
  delete createInventoryInput.notes;
  delete createInventoryInput.product;
  delete createInventoryInput.reloadYardCommodity;
  delete createInventoryInput.shipmentLineItems;
  delete createInventoryInput.zone;

  return createInventoryInput;
};

export const updateLambdaInventoryTransfer = (
  outbound,
  lineItem,
  transferInventory,
) => {
  const sourceInventory = lineItem.inventory;
  const updateDateTime = transferInventory.updatedAt;
  const historyNote = transferInventory.history[0];
  const newQuantityOnHand = trimDecimal(
    sourceInventory.quantityOnHand - lineItem.totalQuantityToPick,
    3,
  );

  const updateInventoryInput = {
    ...sourceInventory,
    updatedAt: updateDateTime,
    quantityOnHand: newQuantityOnHand,
    quantityPhysical: newQuantityOnHand,
  };

  if (newQuantityOnHand === 0) {
    updateInventoryInput.status = InventoryStatus.SHIPPED;
    updateInventoryInput.quantityCommitted = 0;
    updateInventoryInput.depletedAt = updateDateTime;
    updateInventoryInput.lastOutboundId = outbound.id;
    updateInventoryInput.turnDays = differenceInDays(
      new Date(updateDateTime),
      new Date(sourceInventory.receivedAt),
    );
  }

  updateInventoryInput.history = [...updateInventoryInput.history, historyNote];

  delete updateInventoryInput.account;
  delete updateInventoryInput.area;
  delete updateInventoryInput.inboundShipment;
  delete updateInventoryInput.location;
  delete updateInventoryInput.notes;
  delete updateInventoryInput.photos;
  delete updateInventoryInput.product;
  delete updateInventoryInput.reloadYardCommodity;
  delete updateInventoryInput.shipmentLineItems;
  delete updateInventoryInput.zone;

  return updateInventoryInput;
};

export const createLambdaAddress = (outbound) => {
  const shipTo = outbound.shipTo;

  if (shipTo) {
    return {
      id: uuidv4(),
      owner: window.appUser.owner,
      groups: outbound.groups,
      accountId: outbound.billToAccountId || outbound.accountId,
      reloadYardId: outbound.reloadYardId,
      ...shipTo,
    };
  }

  return null;
};

/**
 * Fetch a single Inventory by ID; logs and records an error on failure.
 */
export async function fetchValidationInventory(id) {
  const resp = await API.graphql(
    graphqlOperation(getInventorieValidate, { id }),
  );
  return resp.data.getInventorie;
}

/**
 * Fetch a single InventoryItem by ID; logs and records an error on failure.
 */
export async function fetchValidationInventoryItem(id) {
  const resp = await API.graphql(
    graphqlOperation(getInventoryItemValidate, { id }),
  );
  return resp.data.getInventoryItem;
}

/**
 * Check whether any of the listed date fields was newly set (falsy → truthy)
 * or newly cleared (truthy → falsy) between initial and current form values.
 */
function isSetCleared(fields, initial, formData) {
  return fields.some((fld) => {
    const initVal = initial[fld] || "";
    const currVal = formData[fld] || "";
    const newlySet = !initVal && !!currVal;
    const newlyCleared = !!initVal && !currVal;
    return newlySet || newlyCleared;
  });
}

async function validateSLIInventoryAndItems(formSLI, staleErrors) {
  // Validate SLI’s Inventory
  const formInv = formSLI.inventory;
  const dbInv = await fetchValidationInventory(formInv.id);
  if (formInv && dbInv) {
    if (new Date(dbInv.updatedAt) > new Date(formInv.updatedAt)) {
      staleErrors.push(`Inventory ${formInv?.productName || ""} changed.`);
    }
  }

  // Validate each existing SLIII.inventoryItem
  for (const formSLIII of formSLI.inventoryItems?.items || []) {
    const invItemId = formSLIII.inventoryItem?.id;
    if (invItemId) {
      const dbInvItem = await fetchValidationInventoryItem(invItemId);
      if (dbInvItem) {
        if (
          new Date(dbInvItem.updatedAt) >
          new Date(formSLIII.inventoryItem.updatedAt)
        ) {
          staleErrors.push(
            `Location record ${
              formSLIII.inventoryItem.locationFullName || ""
            } changed.`,
          );
        }
      }
    }
  }
}

/**
 * Refuses a save that would commit inventory still sitting in a released
 * railcar (utils/releasedRailcar.js). The pick dialog already hides such
 * inventory, but it hides it from the list loaded when the form opened, and
 * the car may have been released since — a release does not touch the
 * inventory row, so the stale-record check above cannot catch it either.
 * Each inventory with a fresh commit is re-read once.
 *
 * Only fresh commits are checked (items still NEW with a quantity to pick):
 * a load that already holds such inventory must stay editable so the manager
 * can take it off.
 *
 * @param {Array} lineItems - Pick list rows as the form holds them: an
 *   outbound's shipmentLineItems.items, or the single row a pick-list item
 *   dialog is saving.
 * @returns {Promise<string|null>} Refusal to show the manager, or null.
 */
export const validateReleasedRailcarCommit = async (lineItems) => {
  const blockedCars = [];
  const seenInventoryIds = new Set();

  for (const lineItem of lineItems || []) {
    const freshItems = (lineItem?.inventoryItems?.items || []).filter(
      (item) =>
        item?.status === ShipmentLineItemStatus.NEW &&
        Number(item.quantityToPick || 0) > 0,
    );
    const inventoryId = lineItem?.inventoryId || lineItem?.inventory?.id;
    if (!freshItems.length || !inventoryId) continue;
    if (seenInventoryIds.has(inventoryId)) continue;
    seenInventoryIds.add(inventoryId);

    let inventory;
    try {
      const result = await API.graphql(
        graphqlOperation(getInventorieReleaseState, { id: inventoryId }),
      );
      inventory = result?.data?.getInventorie;
    } catch (error) {
      logError(
        "outboundShipment:outboundUtils:validateReleasedRailcarCommit",
        error,
      );
      // Fail closed: a guard that cannot read the car's status must not wave
      // the pick through.
      return "Could not verify the railcar is still in the yard. Refresh the page and try again.";
    }
    if (!inventory) continue;

    const onReleasedCar = freshItems.some((item) =>
      isInventoryItemOnReleasedRailcar(inventory, item.inventoryItem || item),
    );
    if (onReleasedCar) blockedCars.push(releasedRailcarLabel(inventory));
  }

  return releasedRailcarCommitRefusal(blockedCars);
};

/**
 * validateOutboundBeforeUpdate(formState):
 *
 * - Always compare outbound.updatedAt (if formData.id exists).
 * - If any “dispatch” date (placement, start, end, departure) was newly set or cleared:
 *     • For each EXISTING SLI: compare SLI.updatedAt.
 * - If “departureDateTime” was newly set or cleared:
 *     • For each EXISTING SLI: compare Inventory.updatedAt.
 *     • For each EXISTING SLIInventoryItem: compare its updatedAt.
 * - If any new SLIs were added (itemsAreDirty) OR departureDateChanged:
 *     • For each NEW SLI: compare Inventory.updatedAt.
 *     • For each NEW SLIInventoryItem: compare InventoryItem.updatedAt.
 *
 * Returns { staleErrors: string[] }.
 */
export async function validateOutboundBeforeUpdate(formState, notify) {
  const formData = formState.values;
  const initial = formState.initialValues || {};
  const staleErrors = [];

  // ——— 1) Always validate outbound.updatedAt ———
  let dbOutbound = null;
  try {
    const result = await API.graphql(
      graphqlOperation(getOutboundShipmentValidate, { id: formData.id }),
    );
    dbOutbound = result.data.getOutboundShipment;
    if (!dbOutbound) {
      staleErrors.push(`Outbound shipment deleted.`);
      // abort further validation
      return { staleErrors };
    }
    if (new Date(dbOutbound.updatedAt) > new Date(formData.updatedAt)) {
      staleErrors.push(`Outbound shipment changed.`);
    }

    // ——— 2) Determine which date‐fields were newly set/cleared ———
    const dispatchDateFields = [
      "placementDateTime",
      "startDateTime",
      "endDateTime",
      "departureDateTime",
    ];
    const dispatchDateChanged = isSetCleared(
      dispatchDateFields,
      initial,
      formData,
    );
    const departureDateChanged = isSetCleared(
      ["departureDateTime"],
      initial,
      formData,
    );

    // ——— 3) Check if shipmentLineItems were added/removed (dirty) ———
    const dirty = formState.dirtyFields || [];
    const itemsAreDirty =
      dirty["shipmentLineItems"] === true ||
      dirty["shipmentLineItems.items"] === true;

    // ——— 4) If no dispatch change AND no new Pick List items, skip validation ———
    if (!dispatchDateChanged && !itemsAreDirty) {
      return { staleErrors };
    }

    // ——— Prepare database maps for lookups ———
    const dbSLIs = dbOutbound.shipmentLineItems?.items || [];
    const dbSLIById = {};
    dbSLIs.forEach((li) => {
      dbSLIById[li.id] = li;
    });

    // ——— 5) Loop through each “formSLI” to validate existing/new items ———
    for (const formSLI of formData.shipmentLineItems?.items || []) {
      const dbSLI = dbSLIById[formSLI.id];
      const isNew = formSLI.status === ShipmentLineItemStatus.NEW;

      // — 5a) validate existing SLI.updatedAt
      if (!isNew) {
        if (!dbSLI) {
          staleErrors.push(
            `Pick List item ${formSLI.inventory?.productName || ""} deleted.`,
          );
          continue;
        }
        if (new Date(dbSLI.updatedAt) > new Date(formSLI.updatedAt)) {
          staleErrors.push(
            `Pick List item ${formSLI.inventory?.productName || ""} changed.`,
          );
        }
      }

      // — 5b) If departureDateChanged → validate existing SLI → Inventory.updatedAt
      if (isNew || departureDateChanged) {
        await validateSLIInventoryAndItems(formSLI, staleErrors);
      }
    }
  } catch (error) {
    logError(
      "outboundShipment:outboundUtils:validateOutboundBeforeUpdate",
      error,
    );
    return {
      staleErrors: [
        "Could not verify the record is up to date. Refresh the page and try again.",
      ],
    };
  }

  return { staleErrors };
}

/**
 * Validate outbound on CREATE (formData.id is falsy).
 *
 * - For each new SLI:
 *     • Compare Inventory.updatedAt
 *     • For each new SLIInventoryItem: compare InventoryItem.updatedAt
 */
export async function validateOutboundBeforeCreate(formState, notify) {
  const formData = formState.values;
  const staleErrors = [];

  try {
    for (const formSLI of formData.shipmentLineItems?.items || []) {
      await validateSLIInventoryAndItems(formSLI, staleErrors);
    }
  } catch (err) {
    logError(
      "outbound:outboundUtils:validateOutboundBeforeCreate",
      err,
      notify,
    );
  }

  return { staleErrors };
}

/**
 * Derive the appointment operation a save implies, and point `outbound`'s
 * schedule link at the result.
 *
 * The link is re-set on the passed object on purpose: the caller hands that
 * record back to react-admin's cache, and a stale timeslotId sends the next
 * save down processOutbound's create-appointment branch, which fails its
 * attribute_not_exists condition.
 */
const buildOutboundTimeslotOp = (record, outbound, updateDateTime) => {
  if (record.timeslotId) {
    if (!outbound.scheduledDateTime) {
      // if previously scheduled, then delete
      outbound.timeslotId = null; // update outbound to be saved with transform()
      return deleteLambdaAppointment(record.timeslotId);
    }
    // Normalised on both sides: the loaded record holds an ISO string while
    // the scheduler puts a Date on the form, so a raw !== was true even when
    // both named the same instant — every re-pick of the same slot wrote a
    // pointless appointment update.
    if (
      toComparableScheduledDateTime(record.scheduledDateTime) !==
      toComparableScheduledDateTime(outbound.scheduledDateTime)
    ) {
      // if previously scheduled, then re-schedule
      outbound.timeslot.id = record.timeslotId;
      outbound.timeslot.updatedAt = updateDateTime;
      return updateLambdaAppointment(outbound.timeslot);
    }
    return null;
  }

  if (outbound.timeslot) {
    // create timeslot if previously not scheduled
    const timeslotInput = createLambdaAppointment(
      outbound.timeslot,
      null,
      record.id,
      record.accountId,
      record.accountName,
    );
    // update outbound to be saved with transform()
    outbound.timeslotId = timeslotInput.id;
    return timeslotInput;
  }

  return null;
};

/**
 * Move an appointment and nothing else.
 *
 * The carrier dispatch form has exactly one editable field, so its save has no
 * business rewriting the shipment header, the pick list or inventory — which
 * is what routing it through updateLambdaOutbound's full-record path did,
 * reverting any yard edit made while the form sat open and turning a one-field
 * save into a transaction the size of the pick list. Only scheduledDateTime
 * travels from the form; every other value in the payload is read from
 * `record` (the loaded DB state) so the Lambda can rebuild the index key that
 * embeds it, under a condition on the status and schedule link it was read at.
 *
 * @param {object} record - previousData, i.e. the shipment as loaded
 * @param {object} outbound - the form's data (only scheduledDateTime/timeslot used)
 * @param {object[]} historyAppendList - notes to append, never a full history
 */
export const updateLambdaOutboundSchedule = async (
  record,
  outbound,
  { historyAppendList = [] } = {},
) => {
  const updateDateTime = new Date().toISOString();
  const timeslotInput = buildOutboundTimeslotOp(
    record,
    outbound,
    updateDateTime,
  );

  // The appointment did not move, so there is nothing this path may write:
  // re-stamping the header would be exactly the stale write it exists to avoid.
  // Said out loud rather than resolved quietly — see the inbound twin. This
  // branch was unreachable until the comparison in buildOutboundTimeslotOp
  // stopped testing a string against a Date, which made every re-pick look
  // like a move and write a pointless update instead.
  if (!timeslotInput) {
    const unchanged = new Error(
      "This pickup is already scheduled for the time you picked — nothing to save.",
    );
    unchanged.userFacing = true;
    throw unchanged;
  }

  const outboundScheduleUpdateInput = buildOutboundScheduleUpdateInput(
    { ...record, scheduledDateTime: outbound.scheduledDateTime },
    {
      timeslotId:
        timeslotInput.transactType === "delete" ? null : timeslotInput.id,
      expectedCurrentTimeslotId: record.timeslotId || null,
      // From `record`, never from the form: this is the appointment the carrier
      // was looking at when they hit Save. Rescheduling keeps the same timeslot
      // row, so without this a second form opened on the same booking passes
      // every condition and overwrites the first move without a word.
      expectedCurrentScheduledDateTime: record.scheduledDateTime || null,
      expectedStatus: record.status,
      historyAppendList,
    },
  );

  return await API.post("transREST", "/outboundUpdate", {
    body: {
      outboundScheduleUpdateInput,
      timeslotInput,
      user: window.appUser.email,
      reloadYardId: window.appUser.reloadYardId,
    },
  });
};

export const updateLambdaOutbound = async (
  record,
  outbound,
  isAdvance,
  isRollback,
  lambdaUpdate = false,
  {
    fullUpdate = false,
    scheduleOnly = false,
    historyAppendList,
    trackCarPullId = null,
    trackCarRestoreId = null,
  } = {},
) => {
  // Schedule-only saves never touch the pick list, charges or the track car:
  // they get their own narrow, conditional transaction.
  if (scheduleOnly) {
    return await updateLambdaOutboundSchedule(record, outbound, {
      historyAppendList,
    });
  }

  const updateDateTime = new Date().toISOString();
  const outboundUpdateInput =
    lambdaUpdate && !fullUpdate ? { ...outbound } : undefined;
  const outboundFullUpdateInput = fullUpdate
    ? sanitizeOutboundLambda({ ...outbound })
    : null;

  // process addressbook save
  let addressCreateInput = null;
  if (outbound.saveAddress && isFullAddress(outbound.shipTo)) {
    addressCreateInput = createLambdaAddress(outbound);
  }

  debugger;
  // process appointment timeslot
  const timeslotInput = buildOutboundTimeslotOp(
    record,
    outbound,
    updateDateTime,
  );

  if (outboundFullUpdateInput) {
    // The timeslot block above mutates outbound.timeslotId ("update outbound
    // to be saved") — refresh the snapshot so the parent write persists it.
    outboundFullUpdateInput.timeslotId = outbound.timeslotId ?? null;
    // Form-only flag, consumed above for addressCreateInput — never persist.
    delete outboundFullUpdateInput.saveAddress;
  }

  // process picklist
  const values = transformLambdaPickList(
    outbound,
    isAdvance,
    isRollback,
    updateDateTime,
  );

  const manualChargeOps = buildManualChargeOps(
    outbound,
    record?.manualCharges?.items || [],
    outbound?.manualCharges?.items || outbound?.manualCharges || [],
  );

  if (outboundUpdateInput) {
    delete outboundUpdateInput.account;
    delete outboundUpdateInput.area;
    delete outboundUpdateInput.invoice;
    delete outboundUpdateInput.location;
    delete outboundUpdateInput.reloadYardCommodity;
    delete outboundUpdateInput.shipmentLineItems;
    delete outboundUpdateInput.timeslot;
  }

  // console.log("outboundUpdateInput", outboundUpdateInput);
  // console.log("accountUpdateinput", accountUpdateinput);
  // console.log("shipmentLineItemCreateList", values.shipmentLineItemCreateList);
  // console.log("shipmentLineItemUpdateList", values.shipmentLineItemUpdateList);
  // console.log("shipmentItemInventoryItemCreateList", values.shipmentItemInventoryItemCreateList);
  // console.log("shipmentItemInventoryItemUpdateList", values.shipmentItemInventoryItemUpdateList);
  // console.log("inventoryItemUpdateList", values.inventoryItemUpdateList);
  // console.log("locationUpdateList", values.locationUpdateList);
  // console.log("inventoryUpdateList", values.inventoryUpdateList);

  const badInventories = values.inventoryUpdateList.filter(
    (item) => item.quantityCommitted < 0,
  );
  const badInventoryItems = values.inventoryItemUpdateList.filter(
    (item) => item.quantityCommitted < 0,
  );
  if (badInventories?.length > 0) {
    badInventories.forEach((item) => logError("NEGATIVE INVENTORY", item));
  }
  if (badInventoryItems?.length > 0) {
    badInventoryItems.forEach((item) =>
      logError("NEGATIVE INVENTORYITEM", item),
    );
  }
  if (badInventories?.length > 0 || badInventoryItems?.length > 0) {
    logError("NEGATIVE QTY RECORD", {
      record: record,
      isAdvance: isAdvance,
      isRollback: isRollback,
      lambdaUpdate: lambdaUpdate,
    });
    logError("NEGATIVE QTY OUTBOUND", outbound);
  }

  let trackCarDeleteInput = null;
  let trackCarCreateInput = null;
  let trackCarUpdateInput = null;
  if (
    record.carrierType === CarrierType.RAIL &&
    outbound.carrierType === CarrierType.TRUCK &&
    record.id
  ) {
    trackCarDeleteInput = { id: record.id };
  } else if (
    record.carrierType === CarrierType.TRUCK &&
    outbound.carrierType === CarrierType.RAIL &&
    !outbound.trackCar &&
    record.id
  ) {
    // Use "now" for TrackCar createdAt (carrier change), not the shipment’s original createdAt
    const now = new Date().toISOString();
    trackCarCreateInput = buildTrackCarCreateInput({
      ...outbound,
      id: record.id,
      createdAt: now,
    });
  } else if (
    record.carrierType === CarrierType.RAIL &&
    outbound.carrierType === CarrierType.RAIL &&
    record.id &&
    record.railCarNumber !== outbound.railCarNumber
  ) {
    trackCarUpdateInput = buildTrackCarUpdateInput({
      ...outbound,
      id: record.id,
    });
  }

  const response = await API.post("transREST", "/outboundUpdate", {
    body: {
      outboundUpdateInput: outboundUpdateInput,
      outboundFullUpdateInput: outboundFullUpdateInput,
      addressCreateInput: addressCreateInput,
      timeslotInput: timeslotInput,
      shipmentLineItemCreateList: values.shipmentLineItemCreateList,
      shipmentLineItemUpdateList: values.shipmentLineItemUpdateList,
      shipmentItemInventoryItemCreateList:
        values.shipmentItemInventoryItemCreateList,
      shipmentItemInventoryItemUpdateList:
        values.shipmentItemInventoryItemUpdateList,
      inventoryItemUpdateList: values.inventoryItemUpdateList,
      locationUpdateList: values.locationUpdateList,
      inventoryUpdateList: values.inventoryUpdateList,
      manualChargeCreateList: manualChargeOps.createList,
      manualChargeUpdateList: manualChargeOps.updateList,
      manualChargeDeleteList: manualChargeOps.deleteList,
      trackCarDeleteInput: trackCarDeleteInput,
      trackCarCreateInput: trackCarCreateInput,
      trackCarUpdateInput: trackCarUpdateInput,
      // Read and pulled or restored by the lambda inside this transaction; a
      // car already where the save wants it is left alone.
      trackCarPullInput: trackCarPullId ? { id: trackCarPullId } : null,
      trackCarRestoreInput: trackCarRestoreId ? { id: trackCarRestoreId } : null,
      user: window.appUser.email,
      reloadYardId: window.appUser.reloadYardId,
    },
  });

  return response;
};

// used to save with old updatedAt timestamp
export const updateLambdaOutboundMini = async (record) => {
  // sanitize updatedAt
  record.invoiceId = record.invoice?.id || " ";
  const outboundUpdateInput = sanitizeOutbound(record);

  const response = await API.post("transREST", "/outboundUpdate", {
    body: {
      outboundUpdateInput: outboundUpdateInput,
      user: window.appUser.email,
      reloadYardId: window.appUser.reloadYardId,
      // updatedAt: do not set - will generate outbound changed on BOL generate
    },
  });

  if (!response || response.error) {
    throw (
      (response && response.error) ||
      new Error("Invalid /outboundUpdate response")
    );
  }

  return response;
};

export const getActualWeight = (formData) => {
  if (
    isWeightUOM(formData.totalUOM) &&
    formData.shipmentLineItems?.items.length > 0
  ) {
    const shipmentLineItem = formData.shipmentLineItems.items[0];
    const commodity = shipmentLineItem.inventory.reloadYardCommodityId;
    if (isBulkCommodity(commodity)) {
      if (formData.tareWeight && formData.ladenWeight) {
        const inventoryWeight = trimDecimal(
          formData.ladenWeight - formData.tareWeight,
          3,
        );
        const totalQuantityToPick = trimDecimal(
          shipmentLineItem.totalQuantityToPick,
          3,
        );
        if (inventoryWeight === totalQuantityToPick) return -1;
        return inventoryWeight;
      }
    }
  }
  return -1;
};

export const transformLambdaPickList = (
  outbound,
  isAdvance,
  isRollback,
  updateDateTime,
) => {
  const shipmentLineItemCreateList = [];
  const shipmentLineItemUpdateList = [];
  const shipmentItemInventoryItemCreateList = [];
  const shipmentItemInventoryItemUpdateList = [];
  const inventoryItemUpdateList = [];
  const locationUpdateList = [];
  const locationUpdateMap = {};
  const inventoryUpdateList = [];
  const inventoryUpdateMap = {}; // inventoryId -> accumulated update object
  const inventoryItemUpdateMap = {}; // inventoryItemId -> accumulated update object
  const pickList = outbound.shipmentLineItems?.items || [];
  debugger;
  const actualWeight = getActualWeight(outbound);

  pickList.forEach((lineItem) => {
    const isContainerPickup = outbound.containerPickup;
    if (
      lineItem.status === ShipmentLineItemStatus.NEW &&
      (isContainerPickup || lineItem.totalQuantityToPick > 0)
    ) {
      // added new shipment line item,
      // create ShipmentLineItem & ShipmentLineItemInventoryItem
      // update inventoryItems && inventory
      const shipmentLineItemToCreate = createLambdaShipmentLineItem(
        outbound,
        lineItem,
        updateDateTime,
      );
      shipmentLineItemCreateList.push(shipmentLineItemToCreate);
      lineItem.inventoryItems.items.forEach((lineIteminvItem) => {
        const isBulkMobileLocation =
          lineIteminvItem.inventoryItem.location.type === "BULK_MOBILE";
        debugger;
        if (lineIteminvItem.quantityToPick > 0 || isBulkMobileLocation) {
          const lineItemInventoryItemToCreate =
            createLambdaShipmentLineItemInventoryItem(
              shipmentLineItemToCreate,
              lineIteminvItem,
              updateDateTime,
            );
          shipmentItemInventoryItemCreateList.push(
            lineItemInventoryItemToCreate,
          );
          const itemBase =
            inventoryItemUpdateMap[lineIteminvItem.inventoryItem.id];
          const sourceItem = itemBase
            ? { ...lineIteminvItem, inventoryItem: itemBase }
            : lineIteminvItem;
          const toUpdateInventoryItem = updateLambdaInventoryItem(
            outbound,
            sourceItem,
            "create",
            updateDateTime,
          );
          inventoryItemUpdateMap[toUpdateInventoryItem.id] =
            toUpdateInventoryItem;
        }
      });

      debugger;
      const invBase =
        inventoryUpdateMap[lineItem.inventory.id] || lineItem.inventory;
      let toUpdateInventory = {
        ...invBase,
        updatedAt: updateDateTime,
        quantityCommitted: trimDecimal(
          (invBase.quantityCommitted || 0) + lineItem.totalQuantityToPick,
          3,
        ),
      };
      const note = {
        type: NoteType.INVENTORY,
        date: updateDateTime,
        by: window.appUser?.fullName,
        text: `Committed Outbound Qty. of ${lineItem.totalQuantityToPick} ${toUpdateInventory.inventoryUOM}`,
      };
      toUpdateInventory.history = toUpdateInventory.history
        ? [
            ...(Array.isArray(toUpdateInventory.history)
              ? toUpdateInventory.history
              : [toUpdateInventory.history]),
            note,
          ]
        : [note];

      toUpdateInventory = updateLambdaInventory(
        toUpdateInventory,
        lineItem,
        isAdvance,
        updateDateTime,
      );
      inventoryUpdateMap[lineItem.inventory.id] = toUpdateInventory;
    } else {
      // updating existing shipmentLineItems on advance.
      debugger;
      let quantityToPickOrig;
      if (actualWeight !== -1) {
        quantityToPickOrig = lineItem.totalQuantityToPick;
        lineItem.totalQuantityToPick = actualWeight;
      }
      // shipment line item unchanged - update shipment changes such as poNumber & status on advance / rollback
      const shipmentLineItemToUpdate = updateLambdaShipmentLineItem(
        outbound,
        lineItem,
        updateDateTime,
        actualWeight,
      );
      shipmentLineItemUpdateList.push(shipmentLineItemToUpdate);

      // Inventory and InventoryItem quantities move only when this save ships
      // the row. Header edits, PLACED/LOADED advances and rollbacks leave them
      // alone — re-sending the form's copy was a full-field overwrite from a
      // possibly stale snapshot, and it stamped updatedAt so the next save's
      // staleness check could no longer see the clobber.
      const shipsNow =
        isAdvance &&
        shipmentLineItemToUpdate.status === ShipmentLineItemStatus.SHIPPED;

      // on advance or rollback, update ShipmentLineItemInventoryItem; on ship, inventoryItems & inventory
      lineItem.inventoryItems.items.forEach((lineIteminvItem) => {
        if (lineIteminvItem.quantityToPick > 0) {
          let slIIIQuantityToPickOrig;
          if (actualWeight !== -1) {
            slIIIQuantityToPickOrig = lineIteminvItem.quantityToPick;
            lineIteminvItem.quantityToPick = actualWeight;
          }
          const lineItemInventoryItemToUpdate =
            updateLambdaShipmentLineItemInventoryItem(
              shipmentLineItemToUpdate,
              lineIteminvItem,
              updateDateTime,
            );
          shipmentItemInventoryItemUpdateList.push(
            lineItemInventoryItemToUpdate,
          );

          if (shipsNow) {
            const itemBase =
              inventoryItemUpdateMap[lineIteminvItem.inventoryItem.id];
            const sourceItem = itemBase
              ? {
                  ...lineIteminvItem,
                  // restore location (deleted from the accumulated update) so
                  // the bulk-location status check still sees it
                  inventoryItem: {
                    ...itemBase,
                    location: lineIteminvItem.inventoryItem.location,
                  },
                }
              : lineIteminvItem;
            const inventoryItemToUpdate = updateLambdaInventoryItem(
              outbound,
              sourceItem,
              "advance",
              updateDateTime,
              slIIIQuantityToPickOrig,
            );
            inventoryItemUpdateMap[inventoryItemToUpdate.id] =
              inventoryItemToUpdate;
          }

          if (isRollback) {
            let location = lineIteminvItem.inventoryItem.location;
            // if (location.type === LocationType.STRUCTURED) {
            if (isManagedLocation(location)) {
              if (locationUpdateMap[location.id]) {
                location = locationUpdateMap[location.id];
              }
              const locationToUpdate = resetLambdaCapacityUsed(
                location,
                lineItem,
                lineIteminvItem,
                updateDateTime,
              );
              locationUpdateMap[location.id] = locationToUpdate;
            }
          }
        }
      });

      if (shipsNow) {
        const inventoryToUpdate = updateLambdaInventory(
          inventoryUpdateMap[lineItem.inventory.id] || lineItem.inventory,
          shipmentLineItemToUpdate,
          true,
          updateDateTime,
          quantityToPickOrig,
        );
        inventoryUpdateMap[lineItem.inventory.id] = inventoryToUpdate;
      }
    }
  });

  // After processing all items, collect unique location updates
  locationUpdateList.push(...Object.values(locationUpdateMap));
  // ...and unique inventory / inventoryItem updates (accumulated per id so
  // multiple pick-list rows on the same record chain instead of overwrite)
  inventoryUpdateList.push(...Object.values(inventoryUpdateMap));
  inventoryItemUpdateList.push(...Object.values(inventoryItemUpdateMap));

  return {
    shipmentLineItemCreateList: shipmentLineItemCreateList,
    shipmentLineItemUpdateList: shipmentLineItemUpdateList,
    shipmentItemInventoryItemCreateList: shipmentItemInventoryItemCreateList,
    shipmentItemInventoryItemUpdateList: shipmentItemInventoryItemUpdateList,
    inventoryItemUpdateList: inventoryItemUpdateList,
    locationUpdateList: locationUpdateList,
    inventoryUpdateList: inventoryUpdateList,
  };
};

export const handleShipToSelect = (newValue, form, setSelectedAddress) => {
  let stripped = newValue;
  if (newValue) {
    stripped = { ...newValue };
    delete stripped.accountId;
    delete stripped.reloadYardId;
    delete stripped.groups;
    delete stripped.id;
    delete stripped.owner;
    delete stripped.createdAt;
    delete stripped.updatedAt;
  }
  setSelectedAddress && setSelectedAddress(stripped);
  form.change("shipTo", stripped);
};

export const getAddressBook = (
  accountAddressBook,
  operatingAccountAddressBook,
  form,
  selectedAddress,
  setSelectedAddress,
) => {
  const addressBook =
    operatingAccountAddressBook?.length > 0
      ? operatingAccountAddressBook
      : accountAddressBook || [];

  if (addressBook?.length > 0) {
    return (
      <AddressAutocomplete
        options={addressBook}
        value={selectedAddress}
        onChange={(event, newValue) =>
          handleShipToSelect(newValue, form, setSelectedAddress)
        }
      />
    );
  }
  return null;
};

export const getSaveAddressControl = (
  accountAddressBook,
  operatingAccountAddressBook,
  formData,
) => {
  const addressBook =
    operatingAccountAddressBook?.length > 0
      ? operatingAccountAddressBook
      : accountAddressBook;
  const shipTo = formData.shipTo;

  function addressExistsInAddressBook(array, addressToFind) {
    if (!array || array.length === 0) return false;
    return array.some((savedAddr) => isAddressEqual(savedAddr, addressToFind));
  }

  if (isEmptyAddress(shipTo) || addressExistsInAddressBook(addressBook, shipTo))
    return null;
  return (
    <BooleanInput
      source="saveAddress"
      label="Save to Address Book"
      disabled={!isFullAddress(shipTo)}
      helperText={false}
    />
  );
};

export const autogenPickupNumber = async (account) => {
  // counter not incremented as pickupNumber is generated on create and BOL is create on edit.
  const counter = await incrementBOLCounter();
  return `${account.lotNumberPrefix}-${counter}`;
};

export const incrementBOLCounter = async () => {
  debugger;
  const result = await API.graphql(
    graphqlOperation(getBOLCounter, {
      id: window.appUser?.reloadYardId,
    }),
  );
  const counter = ++result.data.getReloadYard.bolCounter;
  await API.graphql(
    graphqlOperation(updateReloadYardMini, {
      input: {
        id: window.appUser?.reloadYardId,
        active: result.data.getReloadYard.active,
        name: result.data.getReloadYard.name,
        bolCounter: counter,
      },
    }),
  );

  return counter;
};

export const fetchSetupData = async (
  setLoading,
  setRailEquipmentChoices,
  setTruckEquipmentChoices,
  notify,
) => {
  try {
    setLoading(true);
    const carrierEquipmentPromise = fetchCarrierEquipmentTypes();

    const carrierEquipmentResp = await carrierEquipmentPromise;
    setRailEquipmentChoices(carrierEquipmentResp.railEquipmentTypes);
    setTruckEquipmentChoices(carrierEquipmentResp.truckEquipmentTypes);
  } catch (err) {
    logError("outboundShipment:OutboundCreate:getAccountData", err, notify);
  } finally {
    setLoading(false);
  }
};

export const getTransferOptions = (account) => {
  if (isInternalAccount(account)) {
    return TransferOptions.filter((itm) => itm.id === TransferType.INTERNAL);
  }
  return TransferOptions;
};

export const getTransferToAccounts = (form, account, accounts) => {
  if (!account || !accounts || !form) return [];
  const transfer = form.getFieldState("transfer").value;
  debugger;

  if (isInternalAccount(account)) {
    return accounts.filter((itm) => !isInternalAccount(itm));
  } else {
    if (transfer && transfer[0] === TransferType.EXTERNAL) {
      return accounts.filter((itm) => isInternalAccount(itm));
    }
  }
  return accounts;
};

export const piecesRenderer = (record) => {
  const pcs = record?.inventory?.pcs || record?.inventory?.product?.pcs;
  if (pcs != null) return Number(pcs).toLocaleString();
  return "";
};

export const handleContainerPickupChange = (
  value,
  form,
  account,
  setIsContainerPickUp,
  setShipmentLineItems,
  notify,
) => {
  if (value) {
    // no need to tarp if ISOtainer pickup
    form.change("mustTarp", false);
  } else {
    // check if tarp needed from account settings
    form.change("mustTarp", account?.mustTarp || false);
  }

  resetPickList(form, setShipmentLineItems, notify);
  setIsContainerPickUp(value);
};

export const handleMustTarpChange = (
  value,
  form,
  setIsContainerPickUp,
  setShipmentLineItems,
  notify,
) => {
  if (value) {
    const fieldState = form.getFieldState("containerPickup");
    form.change("containerPickup", false);
    setIsContainerPickUp(false);
    if (fieldState.value === true) {
      // if containerPickup changed, then clear the pickList
      resetPickList(form, setShipmentLineItems, notify);
    }
  }
};

export const resetPickList = (form, setShipmentLineItems, notify) => {
  const fieldState = form.getFieldState("shipmentLineItems.items");

  setShipmentLineItems && setShipmentLineItems({ items: [] });
  form.change("shipmentLineItems.items", null);
  form.change("shipmentLineItems", null);

  if (fieldState.value?.length > 0 && notify) {
    notify("Pick List was cleared.", "warning");
  }
};

/**
 * Compute auto charges for an inbound from form values or record and charge rules.
 * Use in InboundCreate (values, chargeRules), InboundEdit (values, chargeRules, record), or InboundShow (record, chargeRules).
 * @param {Object} outbound - Form values or inbound record
 * @param {Array} chargeRules - Account charge rules
 * @returns {{ items: Array }}
 */
export const getAutoCharges = (outbound, chargeRules) => {
  if (!outbound || !chargeRules?.length) {
    return { items: [] };
  }
  const shipmentLineItems = outbound.shipmentLineItems?.items || [];

  // Build a minimal outbound-like object from form data
  const outboundLike = {
    id: outbound.id || "preview",
    shipmentLineItems: {
      items: shipmentLineItems.map((sli) => ({
        ...sli,
        totalQuantityToPick: sli.totalQuantityToPick || 0,
        inventory: {
          ...(sli.inventory || {}),
          quantityReceived: sli?.inventory?.quantityReceived || 0,
        },
      })),
    },
    carrierType: outbound.carrierType,
    carrierEquipmentTypeId: outbound.carrierEquipmentTypeId,
    placementDateTime: outbound.placementDateTime,
    departureDateTime: outbound.departureDateTime || new Date(),
    collectedDateTime: outbound.collectedDateTime,
    isCollected: outbound.isCollected,
    totalUOM: outbound.totalUOM,
    areaId: outbound.areaId,
    zoneId: outbound.zoneId,
    reloadYardCommodityId: outbound.reloadYardCommodityId,
  };

  try {
    // Get only AUTO rules
    const autoRules = chargeRules.filter(
      (r) => r.execution === BillingAutomation.AUTO,
    );

    // Calculate line items (this will return auto charges only, no manual)
    const autoOnly = createLineItemsForOutbound(outboundLike, autoRules);
    return { items: autoOnly };
  } catch (error) {
    logError("outboundShipments:OutboundUtils:getAutoCharges", error, false);
    return { items: [] };
  }
};

export const isTranfer = (record) => {
  return record?.transfer;
};

export const isInternalTransfer = (record) => {
  const transfer = record?.transfer;

  if (Array.isArray(transfer) && transfer.length > 0) {
    return transfer.includes(TransferType.INTERNAL);
  }

  return transfer === TransferType.INTERNAL;
};

// Single-call outbound create: parent + line items + SLIII + inventory
// commits + photos + track car + charges in one lambda transaction set.
// The lambda is the sole writer of the parent OutboundShipment (shipped as
// outboundCreateInput); AppSync no longer creates the record. `toAccount` is
// required only for the internal-transfer branch (destination account of the
// generated inbound) and is threaded through from the save button's props.
export const createLambdaOutboundShipment = async (
  data,
  { photos = [], selectedTimeslot = null, toAccount = null } = {},
) => {
  const pickList = data.shipmentLineItems?.items || [];
  const manualCharges = data.manualCharges?.items || [];
  const saveAddress = data.saveAddress;
  delete data.saveAddress;

  const lotNumbers = new Set();
  const lotTypes = new Set();
  const yardCommodities = new Set();
  let areaId = null;
  let zoneId = null;
  const updateDateTime = data.updatedAt;

  let outboundUpdateInput = null;
  let trackCarCreateInput = null;
  let inboundCreateInput = null;
  let addressCreateInput = null;
  let timeslotCreateInput = null;
  const shipmentLineItemCreateList = [];
  const shipmentItemInventoryItemCreateList = [];
  const inventoryCreateList = [];
  const inventoryUpdateList = [];
  const inventoryItemUpdateList = [];
  const inventoryItemCreateList = [];
  const { createList: manualChargeCreateList } = buildManualChargeOps(
    data,
    [],
    manualCharges,
  );
  if (data.carrierType === CarrierType.RAIL) {
    trackCarCreateInput = buildTrackCarCreateInput(data);
  }
  const photoCreateList = photos.map((p) => ({
    ...p,
    id: uuidv4(),
    __typename: "Photo",
    owner: data.owner,
    parentId: data.id,
    createdAt: data.createdAt,
    updatedAt: data.createdAt,
  }));

  if (isInternalTransfer(data)) {
    // create Shipment LineItems, pickList is required
    pickList.forEach((lineItem) => {
      lotNumbers.add(lineItem.lotNumber);
      lotTypes.add(lineItem.inventory.lotType);
      areaId = lineItem.inventory.areaId ? lineItem.inventory.areaId : areaId;
      zoneId = lineItem.inventory.zoneId ? lineItem.inventory.zoneId : zoneId;
      yardCommodities.add(lineItem.inventory.reloadYardCommodityId);
    });

    // create InboundShipment
    inboundCreateInput = createLambdaInboundTransfer(
      data,
      toAccount,
      lotTypes,
      lotNumbers,
      yardCommodities,
      areaId,
      zoneId,
      updateDateTime,
    );

    const inventoryUpdateMap = {}; // inventoryId -> accumulated update object
    const inventoryItemUpdateMap = {}; // inventoryItemId -> accumulated update object
    pickList.forEach((lineItem, index) => {
      const toCreateShipmentLineItem = createLambdaShipmentLineItem(
        data,
        lineItem,
        updateDateTime,
        true,
      );
      shipmentLineItemCreateList.push(toCreateShipmentLineItem);

      // Create Inbound Transfer Inventories
      const toCreateInventory = createLambdaInventoryTransfer(
        data,
        inboundCreateInput,
        lineItem,
        index,
        updateDateTime,
      );
      inventoryCreateList.push(toCreateInventory);

      // Update Outbound Transfer Inventories
      const invBase = inventoryUpdateMap[lineItem.inventory.id];
      const toUpdateInventory = updateLambdaInventoryTransfer(
        data,
        invBase ? { ...lineItem, inventory: invBase } : lineItem,
        toCreateInventory,
      );
      inventoryUpdateMap[toUpdateInventory.id] = toUpdateInventory;

      lineItem.inventoryItems.items.forEach((lineIteminvItem) => {
        const toCreateshipmentLineItemInventoryItem =
          createLambdaShipmentLineItemInventoryItem(
            toCreateShipmentLineItem,
            lineIteminvItem,
            updateDateTime,
          );
        shipmentItemInventoryItemCreateList.push(
          toCreateshipmentLineItemInventoryItem,
        );

        // Create Inbound Transfer InventoryItems
        const toCreateInventoryItem = createLambdaInventoryItem(
          data,
          lineIteminvItem,
          toCreateInventory,
          updateDateTime,
        );
        inventoryItemCreateList.push(toCreateInventoryItem);

        // Update Outbound Transfer InventoryItems
        const itemBase =
          inventoryItemUpdateMap[lineIteminvItem.inventoryItem.id];
        const sourceItem = itemBase
          ? {
              ...lineIteminvItem,
              // restore location (deleted from the accumulated update) so
              // the bulk-location status check still sees it
              inventoryItem: {
                ...itemBase,
                location: lineIteminvItem.inventoryItem.location,
              },
            }
          : lineIteminvItem;
        const toUpdateInventoryItem = updateLambdaInventoryItem(
          data,
          sourceItem,
          "transfer",
          updateDateTime,
          true,
        );
        inventoryItemUpdateMap[toUpdateInventoryItem.id] = toUpdateInventoryItem;
      });
    });
    // Collect unique inventory / inventoryItem updates (accumulated per
    // id so multiple pick-list rows on the same record chain instead of
    // overwrite)
    inventoryUpdateList.push(...Object.values(inventoryUpdateMap));
    inventoryItemUpdateList.push(...Object.values(inventoryItemUpdateMap));

    const response = await API.post("transREST", "/outboundCreate", {
      body: {
        outboundCreateInput: sanitizeOutboundLambda(data),
        inboundCreateInput,
        trackCarCreateInput,
        shipmentLineItemCreateList,
        shipmentItemInventoryItemCreateList,
        inventoryUpdateList,
        inventoryCreateList,
        inventoryItemUpdateList,
        inventoryItemCreateList,
        manualChargeCreateList,
        user: window.appUser.email,
        reloadYardId: window.appUser.reloadYardId,
      },
    });
    if (response.error) throw response.error;

    return response;
  } else {
    // regular outbound or external transfer
    const isContainerPickup = data.containerPickup;
    if (pickList) {
      const inventoryUpdateMap = {}; // inventoryId -> accumulated update object
      const inventoryItemUpdateMap = {}; // inventoryItemId -> accumulated update object
      pickList.forEach((lineItem) => {
        if (
          lineItem.status === ShipmentLineItemStatus.NEW &&
          (lineItem.totalQuantityToPick > 0 || isContainerPickup)
        ) {
          const shipmentLineItemInput = createLambdaShipmentLineItem(
            data,
            lineItem,
            updateDateTime,
          );
          shipmentLineItemCreateList.push(shipmentLineItemInput);

          lineItem.inventoryItems.items.forEach((lineIteminvItem) => {
            if (lineIteminvItem.quantityToPick > 0) {
              const inventoryItemInput =
                createLambdaShipmentLineItemInventoryItem(
                  shipmentLineItemInput,
                  lineIteminvItem,
                  updateDateTime,
                );
              shipmentItemInventoryItemCreateList.push(inventoryItemInput);

              const itemBase =
                inventoryItemUpdateMap[lineIteminvItem.inventoryItem.id];
              const sourceItem = itemBase
                ? { ...lineIteminvItem, inventoryItem: itemBase }
                : lineIteminvItem;
              const toUpdateInventoryItem = updateLambdaInventoryItem(
                data,
                sourceItem,
                "create",
                updateDateTime,
              );
              inventoryItemUpdateMap[toUpdateInventoryItem.id] =
                toUpdateInventoryItem;
            }
          });

          const invBase =
            inventoryUpdateMap[lineItem.inventory.id] || lineItem.inventory;
          const toUpdateInventory = {
            ...invBase,
            updatedAt: updateDateTime,
            quantityCommitted: trimDecimal(
              invBase.quantityCommitted + lineItem.totalQuantityToPick,
              3,
            ), // Qty committed already calculated in-form
          };
          const note = {
            type: NoteType.INVENTORY,
            date: updateDateTime,
            by: window.appUser?.fullName,
            text: `Committed Outbound Qty. of ${lineItem.totalQuantityToPick}`,
          };
          toUpdateInventory.history = toUpdateInventory.history
            ? [
                ...(Array.isArray(toUpdateInventory.history)
                  ? toUpdateInventory.history
                  : [toUpdateInventory.history]),
                note,
              ]
            : [note];
          // index keys not changing on outbound create - no inventory status change

          delete toUpdateInventory.account;
          delete toUpdateInventory.inboundShipment;
          delete toUpdateInventory.inventoryItems;
          delete toUpdateInventory.location;
          delete toUpdateInventory.product;
          delete toUpdateInventory.reloadYardCommodity;
          delete toUpdateInventory.shipmentLineItems;
          inventoryUpdateMap[lineItem.inventory.id] = toUpdateInventory;
        }
      });
      // Collect unique inventory / inventoryItem updates (accumulated per
      // id so multiple pick-list rows on the same record chain instead of
      // overwrite)
      inventoryUpdateList.push(...Object.values(inventoryUpdateMap));
      inventoryItemUpdateList.push(...Object.values(inventoryItemUpdateMap));
    }

    if (saveAddress && isFullAddress(data.shipTo)) {
      addressCreateInput = createLambdaAddress(data);
    }

    if (selectedTimeslot) {
      timeslotCreateInput = createLambdaAppointment(
        selectedTimeslot,
        null,
        data.id,
        data.accountId,
        data.accountName,
      );
    }

    const response = await API.post("transREST", "/outboundCreate", {
      body: {
        outboundCreateInput: {
          ...sanitizeOutboundLambda(data),
          ...(timeslotCreateInput
            ? { timeslotId: timeslotCreateInput.id }
            : {}),
        },
        outboundUpdateInput: outboundUpdateInput || null,
        addressCreateInput: addressCreateInput || null,
        timeslotCreateInput: timeslotCreateInput || null,
        shipmentLineItemCreateList: shipmentLineItemCreateList || [],
        shipmentItemInventoryItemCreateList:
          shipmentItemInventoryItemCreateList || [],
        inventoryItemUpdateList: inventoryItemUpdateList || [],
        inventoryUpdateList: inventoryUpdateList || [],
        photoCreateList: photoCreateList || [],
        manualChargeCreateList: manualChargeCreateList || [],
        trackCarCreateInput: trackCarCreateInput || null,
        user: window.appUser.email,
        reloadYardId: window.appUser.reloadYardId,
      },
    });
    if (response.error) throw response.error;

    return response;
  }
};
